import axios from 'axios';
import React from 'react';
import { Button, message, Popover } from 'antd';
import { DateTime } from 'luxon';
import moment from 'moment';
import formToPDF from 'ontraccr-form-to-pdf';
import { FormHelpers, TaskHelpers } from 'ontraccr-common';
import qs from 'query-string';
import * as Sentry from '@sentry/react';

import { INVOICE_DRAWER_ADD_MODE, INVOICE_DRAWER_EDIT_MODE, INVOICE_DRAWER_VIEW_MODE } from '../payables/invoices/invoiceConstants';
import { getFileType, removeTrailingSlash } from '../files/fileHelpers';
import sortByString, {
  getIdMap,
  includesTerm,
  isNullOrUndefined,
  removeDuplicateArrayValues,
  toTitleCase,
} from '../helpers/helpers';
import { sortByObjProperty } from '../common/helpers';
import { currencyFormatter } from '../helpers/inputParsers';

import { getFormById, getTemplateDetails } from './state/forms.actions';
import { decoratePayloadWithLabels } from '../helpers/labels';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';
import { isMaterialsTableField } from './MaterialsMappingColumns';
import {
  dataTypesAllowedInExternalForm,
  dataTypesAllowedInMultiDivisionDropdown,
  dataTypesAllowedInMultiDivisionTable,
  MULTI_DIVISION_ALLOWED_WORKFLOW_ACTION_TYPES,
  MULTI_DIVISION_ALLOWED_WORKFLOW_INFRASTRUCTURE_TYPES,
} from './forms.constants';
import { isAllowedDynamicAttributeType } from './FormBuilder/FormFields/DynamicAttribute/dynamicAttributes.constants';

import './pdfClient';
import { DRAFT_SOURCE_ASSIGNMENT, DRAFT_SOURCE_SELF } from './FormsHome/formsHomeHelpers';

// Need to create a new axios instance,
// because our default attaches a Bearer Token to all requests
const axiosFileInstance = axios.create();

export default {};

export const formatFormTemplateName = (
  formTemplate = {},
  projectIdMap = {},
) => {
  const prefix = formTemplate?.projectId ? `${projectIdMap[formTemplate.projectId]?.name} - ` : '';
  const suffix = formTemplate?.name ?? '';

  return `${prefix}${suffix}`;
};

export const generateId = () => DateTime.local().toMillis();

export const getExternalFormTextList = (texts = []) => {
  const textKeyCounts = {};
  return texts.map((text) => {
    const keyValue = text || 'blank';
    const count = textKeyCounts[keyValue] || 0;
    textKeyCounts[keyValue] = count + 1;
    return {
      key: `external-form-text-${keyValue}-${count}`,
      value: text,
    };
  });
};

export const parseFormDates = (form = {}) => {
  const {
    lastUpdated,
    createdAt,
    initiatedOn,
  } = form;

  const lastUpdatedDate = lastUpdated
    ? DateTime.fromMillis(lastUpdated).toLocaleString(DateTime.DATETIME_MED)
    : null;

  const createdAtDate = createdAt
    ? DateTime.fromMillis(createdAt).toLocaleString(DateTime.DATETIME_MED)
    : null;

  const initiatedOnDate = initiatedOn
    ? DateTime.fromMillis(initiatedOn).toLocaleString(DateTime.DATETIME_MED)
    : null;

  return {
    lastUpdatedDate,
    createdAtDate,
    initiatedOnDate,
  };
};

/**
 * Calculates the count of unique assigned and manual draft forms
 * @param {object} forms - Object containing form data
 * @returns {number} Count of assigned and draft forms
 */
export const getNumberOfAssignedAndDraftForms = ({
  drafts: {
    manual: manualDrafts = {},
  } = {},
  assignedForms = [],
} = {}) => {
  const unReadAssignedForms = assignedForms.filter((form) => !form.isRead);
  const uniqueForms = new Set(unReadAssignedForms.map((form) => form.id));
  Object.values(manualDrafts).forEach((draft) => {
    if (!draft.isRead) {
      uniqueForms.add(draft.id);
    }
  });
  return uniqueForms.size;
};

/**
 * Calculates the count of active form approvals
 * @param {object} formApprovals - Object containing form approval data
 * @returns {number} Count of active form approvals
 */
export const getFormApprovalCount = (formApprovals = {}) => (
  Object.values(formApprovals).filter((approval) => (
    approval.status !== 'Cancelled' && (!approval.editable || approval.editableApprovals) && !approval.isRead
  )).length
);

/**
 * Creates an object with the required metadata for a file.
 * Allows reusing within decorateFile to obtain the full file
 * @param {*} file - the object representing a file
 * @param {*} fileId - the id of the file
 * @returns {object} - the mimic file object
 */
export const decorateShallowFile = (file, fileId) => {
  if (!file) return {};
  const newFile = { ...file };
  const { name, url, thumbnailURL } = file;
  const type = getFileType({ name });
  newFile.jsFileObject = {};
  newFile.jsFileObject.isPreview = true;
  newFile.jsFileObject.existing = true;
  newFile.jsFileObject.id = fileId;
  newFile.jsFileObject.type = type;
  newFile.jsFileObject.name = name;
  newFile.jsFileObject.url = url;
  newFile.jsFileObject.thumbnailURL = thumbnailURL;
  return newFile;
};

export const decorateFile = async (file, fileId, onlyImages, onProgressChange) => {
  if (!file) return null;
  try {
    const fileRef = file;
    const { url, name = 'untitled' } = file;
    const {
      data,
      headers = {},
    } = await axiosFileInstance.get(url, {
      responseType: 'arraybuffer',
      onDownloadProgress: (progressEvent) => {
        if (!onProgressChange) return;
        const { loaded, total } = progressEvent;
        const percent = Math.round((loaded * 100) / total);
        onProgressChange(percent);
      },
    });
    const {
      'content-type': trueType,
    } = headers;
    let type = getFileType({ name });
    if (onlyImages) {
      // PDF Designer only supports these two types.
      // When we try to reupload in createTemplate, we need the full type
      // not just type === 'image'
      type = name.includes('png') ? 'image/png' : 'image/jpeg';
    }
    const jsFileObject = new File([data], name, { type });
    jsFileObject.existing = true;
    jsFileObject.id = fileId ?? file.id;
    jsFileObject.thumbnailURL = file.thumbnailURL;
    fileRef.jsFileObject = jsFileObject;
    fileRef.trueType = trueType;
    return jsFileObject;
  } catch (e) {
    // noop
    return null;
  }
};

const getSignatureIds = (sections = []) => {
  const ids = [];

  sections.forEach((section) => {
    const { fields = [] } = section;
    fields.forEach((field) => {
      const { type, response } = field;
      if (type === 'multiSig') {
        const { values = [] } = response;
        values.forEach((val) => {
          const { sig } = val;
          ids.push(sig);
        });
      }
    });
  });
  return ids;
};

const getPdfDesignerImageIds = (drawOptions = []) => {
  const ids = [];
  drawOptions.forEach((page) => {
    page.forEach((option) => {
      const { type, image } = option;
      if (type === 'image') {
        ids.push(image);
      }
    });
  });
  return ids;
};

const decorateStaticFiles = (sections, fileMap) => {
  sections?.forEach?.((section) => {
    const fields = section?.fields ?? [];
    fields?.forEach?.((field) => {
      if (field?.selectedType !== 'staticAttachments') return;
      const configProps = field?.configProps ?? {};
      const fieldFileIds = configProps?.files ?? [];
      field.configProps = { // eslint-disable-line no-param-reassign
        ...configProps,
        files: fieldFileIds.map((fileId) => fileMap[fileId])
          .filter((file) => !!file),
      };
    });
  });
};

export const decorateFormWithShallowFiles = async (form, {
  templateSections = form?.formData?.sections,
} = {}) => {
  const { fileMap } = form;
  const fileIds = Object.keys(fileMap);
  if (fileIds.length === 0) return;

  fileIds.forEach((fileId) => {
    const file = fileMap[fileId];
    fileMap[fileId] = decorateShallowFile(file, fileId);
  });

  decorateStaticFiles(templateSections, fileMap);

  const sections = form?.sections ?? form?.formData?.sections;
  const signatureIds = getSignatureIds(sections);
  const pdfImageIds = getPdfDesignerImageIds(form?.drawOptions);

  await Promise.all(
    [...signatureIds, ...pdfImageIds].map(async (fileId) => {
      try {
        const file = fileMap[fileId];
        await decorateFile(file, fileId);
      } catch (e) {
        // noop
      }
    }),
  );
};

export const decorateFormWithFiles = async (form, {
  onlyImages, templateSections = form?.formData?.sections,
} = {}) => {
  const { fileMap } = form;
  const fileIds = Object.keys(fileMap);
  if (fileIds.length === 0) return;

  await Promise.all(
    fileIds.map(async (fileId) => {
      try {
        const file = fileMap[fileId];
        await decorateFile(file, fileId, onlyImages);
      } catch (e) {
        // noop
      }
    }),
  );

  decorateStaticFiles(templateSections, fileMap);
};

/**
 * Gets the phased-costcode key
 * @param {string} phaseId
 * @param {string} costcodeId
 * @returns {string}
 */
export const getPhasedCostcodeKey = (phaseId, costcodeId) => `${phaseId}.${costcodeId}`;

/**
 * If the dataType is 'Costcodes' Reconstructs id by combing phaseId with costcodeId
 * @param {string} dataType
 * @param {array} values
 * @returns {array} parsed costcodes
 */
const parseSelectedDropdowns = (dataType, values = []) => {
  if (dataType !== 'Costcodes') return values;
  return values.map(({ id: costcodeId, phaseId, name }) => ({
    id: phaseId ? getPhasedCostcodeKey(phaseId, costcodeId) : `unphased.${costcodeId}`,
    name,
  }));
};

const getStaticAttachmentField = ({
  field = {},
  configProps = {},
  fileIds = [],
  fileMap = {},
  setSelectedFile,
  setSelectedFileDetails,
  getFromMap,
}) => {
  const fullFiles = getFromMap ? [] : fileIds;
  const fullFileMap = [];
  if (getFromMap) {
    fileIds.forEach((fileId, index) => {
      if (fileId in fileMap && fileMap[fileId].jsFileObject) {
        const file = fileMap[fileId];
        fullFiles.push(file);
        fullFileMap.push(index);
      }
    });
  }

  return {
    configProps: {
      ...field,
      ...configProps,
      files: fullFiles,
      onClick: async (idx) => {
        const file = fullFiles[idx]?.jsFileObject;
        setSelectedFile(file);
        if (setSelectedFileDetails) setSelectedFileDetails({ ...field, index: idx });
        const ourFileIndex = fullFileMap[idx];
        if (ourFileIndex < fileIds.length) {
          const ourFileId = fileIds[ourFileIndex];
          await axios.post('/events', { id: ourFileId });
        }
      },
    },
  };
};

// We want these keys to show up no matter what
const tableKeysToMaintain = new Set([
  'id',
  'name',
  'description',
  'price',
  'hourlyCost',
  'hourlyBillingRate',
  'dailyCost',
  'dailyBillingRate',
]);

/**
 * Gets the difference between two responses. If there is no difference, the response is removed
 * Otherwise we show the new value
 */
const getResponseDiff = ({
  type,
  response = {},
  secondaryResponse,
  fileMap,
}) => {
  const newResponse = {
    ...response,
  };

  // These types should ignore the secondary response if it's null
  const setToIgnoreSecondaryResponseNull = new Set(['multiSig', 'attachment']);

  if (!secondaryResponse && !setToIgnoreSecondaryResponseNull.has(type)) {
    return newResponse;
  }

  switch (type) {
    case 'yes-no': {
      const { value, explanation } = response;
      const { value: secondaryValue, explanation: secondaryExplanation } = secondaryResponse;

      if (secondaryValue === value) {
        newResponse.selected = null;
        newResponse.value = null;
      }

      if (secondaryExplanation === explanation) {
        newResponse.explanation = null;
      }

      return newResponse;
    }
    case 'dropdown': {
      const { selected = [] } = response;
      const { selected: secondarySelected = [] } = secondaryResponse;
      const secondarySet = new Set(secondarySelected.map((val) => val.id));
      newResponse.selected = selected.filter((val) => !secondarySet.has(val.id));
      return newResponse;
    }
    case 'multiSig': {
      const { values = [] } = response;
      const { values: secondaryValues = [] } = secondaryResponse ?? {};
      const secondarySet = new Set(secondaryValues.map((val) => val.sig));
      newResponse.values = values
        .filter((val) => !secondarySet.has(val.sig))
        .map((val) => {
          const { sig } = val;
          const res = { ...val };
          if (sig in fileMap && fileMap[sig].jsFileObject) {
            res.sig = fileMap[sig].jsFileObject;
          }
          return res;
        });
      return newResponse;
    }
    case 'attachment': {
      const { fileIds = [], timestamps } = response;
      const { fileIds: secondaryFileIds = [] } = secondaryResponse ?? {};
      const secondarySet = new Set(secondaryFileIds);
      const relevantFileIds = fileIds.filter((fileId) => !secondarySet.has(fileId));
      const fullFiles = [];
      const fullFileMap = [];
      relevantFileIds.forEach((fileId, index) => {
        if (fileId in fileMap && fileMap[fileId].jsFileObject) {
          fullFiles.push(fileMap[fileId].jsFileObject);
          fullFileMap.push(index);
        }
      });
      return {
        fileIds: relevantFileIds,
        fullFiles,
        fullFileMap,
        timestamps,
      };
    }
    case 'attribute':
    case 'text': {
      const { value } = response;
      const { value: secondaryValue } = secondaryResponse;
      if (secondaryValue === value) {
        newResponse.value = null;
      }

      if (value?.startsWith(secondaryValue)) {
        // Remove the secondary value from the new value + a comma
        newResponse.value = value.slice(secondaryValue.length + 1);
      }

      return newResponse;
    }
    case 'dateRange': {
      const { startTime, endTime } = response;
      const { startTime: secondaryStart, endTime: secondaryEnd } = secondaryResponse;
      if (secondaryStart === startTime) {
        newResponse.startTime = null;
      }

      if (secondaryEnd === endTime) {
        newResponse.endTime = null;
      }

      return newResponse;
    }
    case 'dateTime': {
      const { date, time } = response;
      const { date: secondaryDate, time: secondaryTime } = secondaryResponse;

      // If both values are the same, remove them
      if (secondaryDate === date && secondaryTime === time) {
        newResponse.date = null;
        newResponse.time = null;
      }
      return newResponse;
    }
    case 'table': {
      const { values: responseValues = [] } = response;
      const { values: secondaryValues = [] } = secondaryResponse;

      const secondaryResponseMap = secondaryValues?.reduce((acc, row) => {
        acc[row.id] = row;
        return acc;
      }, {});

      newResponse.values = responseValues.map((row) => {
        const previousRow = secondaryResponseMap[row.id];

        // Row was added
        if (!previousRow) return row;
        const rowDiff = {
          ...row,
        };

        let rowHasDiff = false;

        Object.keys(rowDiff).forEach((key) => {
          if (rowDiff[key] !== previousRow[key]) rowHasDiff = true;

          // If the value is the same, remove it
          if (rowDiff[key] === previousRow[key]) {
            // Unless it's a key we want to maintain
            if (!tableKeysToMaintain.has(key)) rowDiff[key] = null;
            // Otherwise if it's a number, we want to show the difference
          } else if (typeof rowDiff[key] === 'number') {
            rowDiff[key] = (rowDiff[key] ?? 0) - (previousRow[key] ?? 0);
            // Otherwise if it's a string, we want to show the difference
          } else if (typeof rowDiff[key] === 'string' && rowDiff[key]?.startsWith(previousRow[key])) {
            rowDiff[key] = rowDiff[key].slice(previousRow[key].length + 1);
          }
        });
        return rowHasDiff ? rowDiff : null;
      }).filter((row) => row);
      return newResponse;
    }
    default: {
      return newResponse;
    }
  }
};

const getNewConditionalRenderingFormula = (fieldIdMap, sectionId) => (formula) => {
  const relevantField = fieldIdMap[formula.field];
  if (relevantField?.sectionId !== sectionId) {
    const fields = Object.values(fieldIdMap);
    const relevantFieldInSection = fields.find((field) => (
      field.duplicatedParentId === formula.field && field.sectionId === sectionId
    ));

    if (relevantFieldInSection) {
      return {
        ...formula,
        field: relevantFieldInSection.id ?? relevantFieldInSection.fieldId,
      };
    }
  }

  return formula;
};

const formatResponseForParsing = ({
  field,
  fieldId,
  fieldTitle,
  templateConfig,
  configPropsMap,
  response,
  secondaryResponse,
  fileMap,
  setSelectedFile,
  setSelectedFileDetails,
}) => ({
  field,
  fieldId,
  fieldTitle,
  templateConfig,
  configPropsMap,
  response,
  secondaryResponse,
  fileMap,
  setSelectedFile,
  setSelectedFileDetails,
});

const parseResponseYesNo = (formattedResponse) => {
  const {
    field, fieldId, templateConfig, configPropsMap, response, secondaryResponse,
  } = formattedResponse;
  const { value, explanation } = response;
  let ans;
  if (value === true) {
    ans = 'yes';
  } else if (value === false) {
    ans = 'no';
  } else if (value === null) {
    ans = 'n/a';
  }
  const parsedResponse = {
    ...response,
    selected: ans,
  };
  return {
    configProps: {
      ...field,
      ...templateConfig,
      ...configPropsMap[fieldId],
      explain: explanation ? [ans] : [],
    },
    response: getResponseDiff({
      type: 'yes-no',
      response: parsedResponse,
      secondaryResponse,
    }),
  };
};

/**
 * @param {object} formattedResponse
 * @param {{ dataType?: string }} [options] — override dropdown dataType (e.g. dynamicAttribute uses fieldDataType)
 */
const parseResponseDropdown = (formattedResponse, options = {}) => {
  const {
    field, fieldId, templateConfig, configPropsMap, response, secondaryResponse,
  } = formattedResponse;
  const { values = [] } = response;
  const dataType = options.dataType ?? templateConfig.dataType;
  return {
    configProps: {
      ...field,
      ...templateConfig,
      ...configPropsMap[fieldId],
      numAnswers: values.length,
    },
    response: getResponseDiff({
      type: 'dropdown',
      response: {
        ...response,
        selected: parseSelectedDropdowns(dataType, values),
      },
      secondaryResponse: secondaryResponse
        ? {
          ...secondaryResponse,
          selected: parseSelectedDropdowns(dataType, secondaryResponse.values),
        }
        : null,
    }),
  };
};

const parseResponseMultiSig = (formattedResponse) => {
  const {
    field, fieldId, templateConfig, configPropsMap, response, secondaryResponse, fileMap,
  } = formattedResponse;
  return {
    configProps: {
      ...field,
      ...templateConfig,
      ...configPropsMap[fieldId],
    },
    response: getResponseDiff({
      type: 'multiSig',
      response,
      secondaryResponse,
      fileMap,
    }),
  };
};

const parseResponseStaticAttachments = (formattedResponse) => {
  const {
    field, fieldId, templateConfig, configPropsMap, response, fileMap,
    setSelectedFile, setSelectedFileDetails,
  } = formattedResponse;
  const fullConfig = {
    ...configPropsMap[fieldId],
    ...templateConfig,
  };
  const fileIds = response?.fileIds ?? fullConfig?.files?.map((file) => file.id) ?? [];
  return getStaticAttachmentField({
    field,
    configProps: fullConfig,
    fileIds,
    fileMap,
    setSelectedFile,
    setSelectedFileDetails,
    getFromMap: true,
  });
};

const parseResponseAttachment = (formattedResponse) => {
  const {
    field, fieldId, templateConfig, configPropsMap, response, secondaryResponse, fileMap,
    setSelectedFile, setSelectedFileDetails,
  } = formattedResponse;
  const {
    fileIds,
    fullFiles = [],
    fullFileMap = [],
  } = getResponseDiff({
    type: 'attachment',
    response,
    secondaryResponse,
    fileMap,
  });
  return {
    configProps: {
      ...field,
      ...templateConfig,
      ...configPropsMap[fieldId],
      showButton: false,
    },
    response: {
      ...response,
      files: fullFiles,
      onClick: async (idx) => {
        setSelectedFile(fullFiles[idx]);
        if (setSelectedFileDetails) setSelectedFileDetails({ ...field, index: idx });
        const ourFileIndex = fullFileMap[idx];
        if (ourFileIndex < fileIds.length) {
          const ourFileId = fileIds[ourFileIndex];
          await axios.post('/events', { id: ourFileId });
        }
      },
      hideDelete: true,
    },
  };
};

const parseResponseGpsLocation = (formattedResponse) => {
  const { field, fieldId, templateConfig, configPropsMap, response } = formattedResponse;
  const { values = [] } = response;
  return {
    configProps: {
      ...field,
      ...templateConfig,
      ...configPropsMap[fieldId],
      numAnswers: values.length,
    },
    response: {
      ...response,
      values,
    },
  };
};

const parseResponseStaticText = (formattedResponse) => {
  const { fieldTitle, templateConfig, configPropsMap, fieldId } = formattedResponse;
  return {
    configProps: { ...templateConfig, ...configPropsMap[fieldId], title: fieldTitle },
  };
};

/** weather, dateRange, dateTime, attribute, text, calculation (and staticText → text for diff). */
const parseResponseTextStyleField = (formattedResponse, diffType) => {
  const {
    field, fieldId, templateConfig, configPropsMap, response, secondaryResponse,
  } = formattedResponse;
  return {
    configProps: {
      columns: response.columns,
      dataType: response.dataType,
      optional: response.optional,
      ...field,
      ...templateConfig,
      ...configPropsMap[fieldId],
    },
    response: getResponseDiff({
      type: diffType,
      response,
      secondaryResponse,
    }),
  };
};

const parseResponseTable = (formattedResponse) => {
  const {
    field, fieldId, templateConfig, configPropsMap, response, secondaryResponse,
    setSelectedFile, setSelectedFileDetails,
  } = formattedResponse;
  const tableResponse = getResponseDiff({
    type: 'table',
    response,
    secondaryResponse,
  });
  const values = response?.values ?? [];
  return {
    configProps: {
      columns: response.columns,
      dataType: response.dataType,
      optional: response.optional,
      ...field,
      ...templateConfig,
      ...configPropsMap[fieldId],
    },
    response: {
      ...tableResponse,
      onFileClick: (rowId, fileIndex) => {
        const row = values.find((r) => r.id === rowId);
        const file = row?.attachments?.[fileIndex];
        if (file) setSelectedFile(file);
        if (setSelectedFileDetails) {
          setSelectedFileDetails({
            ...field,
            index: fileIndex,
            response: {
              files: row?.attachments || [],
              fileIds: row?.attachments?.map((a) => a.id) || [],
            },
          });
        }
      },
    },
  };
};

const parseResponseCalculationDefault = (formattedResponse) => {
  const { field, fieldId, templateConfig, configPropsMap, response } = formattedResponse;
  return {
    configProps: {
      ...field,
      ...templateConfig,
      ...configPropsMap[fieldId],
    },
    response,
  };
};

const parseResponse = ({
  field,
  type,
  response = {},
  fileMap,
  setSelectedFile,
  setSelectedFileDetails,
  templateMap = {},
  configPropsMap = {},
  secondaryFormFieldMap,
  fieldMap,
}) => {
  const {
    fieldId,
    sectionId,
    title: fieldTitle,
    duplicateParentSectionId,
    duplicatedParentId,
  } = field;

  const {
    [duplicateParentSectionId ?? sectionId]: {
      [duplicatedParentId ?? fieldId]: {
        configProps: templateConfig = {},
      } = {},
    } = {},
  } = templateMap;

  if (
    templateConfig?.hasConditionalRendering
    && FormHelpers.isConditionalRenderingFormulaComplete(
      templateConfig.conditionalRenderingFormula,
    )
  ) {
    const formula = templateConfig.conditionalRenderingFormula;

    if (Array.isArray(formula)) {
      templateConfig.conditionalRenderingFormula = formula.map(
        getNewConditionalRenderingFormula(fieldMap, sectionId),
      );
    } else {
      templateConfig.conditionalRenderingFormula = getNewConditionalRenderingFormula(
        fieldMap,
        sectionId,
      )(formula);
    }
  }

  const { response: secondaryResponse } = secondaryFormFieldMap?.[fieldId] ?? {};

  const formattedResponse = formatResponseForParsing({
    field,
    fieldId,
    fieldTitle,
    templateConfig,
    configPropsMap,
    response,
    secondaryResponse,
    fileMap,
    setSelectedFile,
    setSelectedFileDetails,
  });

  let fieldType = type;
  if (fieldType === 'dynamicAttribute') {
    const {
      templateConfig: templateConfigFromFormattedResponse,
      response: responseFromFormattedResponse,
    } = formattedResponse;
    const underlyingTypeFromConfig = templateConfigFromFormattedResponse?.fieldType;
    // If the field type is not set in the template config (mostly for attributes),
    // use the response values to determine the field type
    const underlyingType = underlyingTypeFromConfig
      ?? (Array.isArray(responseFromFormattedResponse?.values) ? 'dropdown' : 'text');
    fieldType = underlyingType;
  }

  switch (fieldType) {
    case 'yes-no':
      return parseResponseYesNo(formattedResponse);
    case 'dropdown':
      return parseResponseDropdown(formattedResponse);
    case 'multiSig':
      return parseResponseMultiSig(formattedResponse);
    case 'staticAttachments':
      return parseResponseStaticAttachments(formattedResponse);
    case 'attachment':
      return parseResponseAttachment(formattedResponse);
    case 'gpsLocation':
      return parseResponseGpsLocation(formattedResponse);
    case 'staticText':
      return parseResponseStaticText(formattedResponse);
    case 'weather':
    case 'dateRange':
    case 'dateTime':
    case 'attribute':
    case 'text':
      return parseResponseTextStyleField(formattedResponse, fieldType);
    case 'table':
      return parseResponseTable(formattedResponse);
    case 'calculation':
    default:
      return parseResponseCalculationDefault(formattedResponse);
  }
};

export const parseFormTemplate = ({
  sections = [],
  fileMap = {},
  setSelectedFile,
  setSelectedFileDetails,
}) => (
  sections.map((section) => {
    const fields = section?.fields ?? [];
    return {
      ...section ?? {},
      fields: fields.map((field) => {
        if (field.selectedType !== 'staticAttachments') return field;
        return {
          ...field,
          ...getStaticAttachmentField({
            field: {
              response: {
                fileIds: field.configProps?.files?.map((file) => file.id) ?? [],
              },
            },
            configProps: field.configProps,
            fileIds: field.configProps?.files,
            fileMap,
            setSelectedFile,
            setSelectedFileDetails,
          }),
        };
      }),
    };
  })
);

export const parseCompletedForm = ({
  sections = [],
  fileMap = {},
  templateMap = {},
  setSelectedFile,
  setSelectedFileDetails,
  hideFieldsWithNoTemplate,
  hideTimeCardSection,
  configPropsMap = {},
  secondaryFormFieldMap, // if we want to compare the response to a secondary form
}) => {
  const fieldMap = sections.reduce((acc, section) => {
    const { fields = [] } = section;
    fields?.forEach((field) => {
      const { fieldId } = field;
      acc[fieldId] = field;
    });
    return acc;
  }, {});

  return sections
    .filter((section, sectionIdx) => {
      const {
        fields = [],
      } = section;
      const [{ sectionId = `section-${sectionIdx}` } = {}] = fields;
      if (sectionId === 'timecard' && hideTimeCardSection) return false;
      return !hideFieldsWithNoTemplate || sectionId in templateMap;
    })
    .map((section, sectionIdx) => {
      const {
        name,
        fields = [],
        duplicatedParentId,
        settings = {},
        id: sectionId,
      } = section;
      const [{ sectionId: fieldSectionId = `section-${sectionIdx}` } = {}] = fields;
      const templateSettings = templateMap[sectionId]?._Settings;
      const ourPermissions = templateSettings
        ? templateSettings.permissions
        : settings?.permissions;

      const relevantSectionId = duplicatedParentId ?? sectionId ?? fieldSectionId;
      const relevantSettings = templateMap[relevantSectionId]?.settings;
      return {
        id: relevantSectionId,
        name,
        settings: {
          ...(relevantSettings ?? settings ?? {}),
          permissions: ourPermissions,
        },
        fields: fields.filter((field) => {
          const { fieldId, sectionId: fieldSection } = field;
          return !hideFieldsWithNoTemplate
          || (fieldSection in templateMap && fieldId in templateMap[fieldSection]);
        })
          .map((field, fieldIdx) => (
            {
              id: field.fieldId ?? `field-${fieldIdx}`,
              fieldId: field.fieldId ?? `field-${fieldIdx}`,
              sectionId: field.sectionId,
              test: configPropsMap[field.fieldId],
              type: field.type,
              selectedType: field.type,
              ...parseResponse({
                field: {
                  ...field,
                  duplicateParentSectionId: duplicatedParentId,
                },
                type: field.type,
                response: field.response,
                fileMap,
                setSelectedFile,
                setSelectedFileDetails,
                templateMap,
                hideFieldsWithNoTemplate,
                configPropsMap,
                secondaryFormFieldMap,
                fieldMap,
              }),
            }
          )),
      };
    });
};

export const createSourceToTargetMap = (targetToSourceMap = {}) => {
  const sourceFieldMap = {};
  Object.keys(targetToSourceMap).forEach((targetId) => {
    const sourceId = targetToSourceMap[targetId];
    if (!(sourceId in sourceFieldMap)) sourceFieldMap[sourceId] = [];
    sourceFieldMap[sourceId].push(targetId);
  });
  return sourceFieldMap;
};

export const createTargetToSourceMap = (sourceToTargetMap = {}) => {
  const targetFieldMap = {};
  Object.keys(sourceToTargetMap).forEach((sourceId) => {
    const targetIds = sourceToTargetMap[sourceId];
    targetIds.forEach((targetId) => {
      targetFieldMap[targetId] = sourceId;
    });
  });
  return targetFieldMap;
};

const linkToDataType = {
  projectId: 'Projects',
  customerId: 'Customers',
  userId: 'Users',
  equipmentId: 'Equipment',
};

const getTargetFieldFromSections = (targetId, sections) =>
  sections.flatMap((s) => s?.fields ?? []).find((f) => f.id === targetId) ?? null;

const filterFullyAllocatedMaterialsRows = (response) => {
  const rows = response?.values ?? [];
  return { ...response, values: rows.filter(({ quantity, quantityAllocated }) =>
    isNullOrUndefined(quantity) || isNullOrUndefined(quantityAllocated) || quantity < quantityAllocated,
  ) };
};

const resetMaterialsRowsAddedDate = (response) => {
  const rows = response?.values ?? [];
  const addedDate = TaskHelpers.formatDate(DateTime.local().toMillis());
  return {
    ...response,
    values: rows.map((row) => ({ ...row, addedDate })),
  };
};

export const fillFormFromProfile = ({
  profileType,
  formTemplate = {},
  customData = [],
  nativeData = {},
  mappings = {},
  projects = {},
  customers = {},
  users = {},
  equipment = {},
  profileId,
  circularFieldIds = [],
  cardLink = {},
  nativeFields = [],
  excludeMaterialsRowsMap = {},
  resetMaterialsAddedDateMap = {},
}) => {
  const fieldMappings = createSourceToTargetMap(mappings);

  const {
    formData: {
      name: templateName,
      sections: newTemplateSections = [],
    } = {},
  } = formTemplate;

  const typeDataMap = {
    projectId: projects,
    customerId: customers,
    userId: users,
    equipmentId: equipment,
  };

  const {
    linkType,
    linkId,
  } = cardLink || {};
  let linkAssigned = false;

  const projectIdSet = new Set();
  const customerIdSet = new Set();
  let formVendorId;
  let formCostcodeId;

  const responseTargetMap = {};
  customData.forEach((section) => {
    const { fields = [] } = section;
    fields.forEach((field) => {
      const { fieldId, response, type } = field;
      if (fieldId in fieldMappings) {
        const targetFieldIds = fieldMappings[fieldId];
        targetFieldIds.forEach((targetId) => {
          const targetField = getTargetFieldFromSections(targetId, newTemplateSections);
          const targetDataType = targetField?.configProps?.dataType;
          if (type === 'table') {
            // Apply materials row filtering when applicable (regardless of circular)
            let tableResponse = response;
            const targetColumns = targetField?.configProps?.columns ?? [];

            if (targetDataType === 'Materials') {
              tableResponse = FormHelpers.applyQuantityRemainingForMappedMaterials(
                tableResponse,
                targetColumns,
              );
            }

            if (isMaterialsTableField(targetField) && excludeMaterialsRowsMap[targetId]) {
              tableResponse = filterFullyAllocatedMaterialsRows(tableResponse);
            }
            // Removes columns from table fields that are part of a circular mapping
            if (circularFieldIds.includes(targetId)) {
              const columnsToRemove = FormHelpers.BLANK_MAPPED_COLUMNS[targetDataType];
              tableResponse = FormHelpers.removeColumnsFromTable(tableResponse, columnsToRemove);
            }

            // Map custom columns to the table response
            tableResponse = FormHelpers.mapTableResponseForTargetField(tableResponse, targetField);

            if (isMaterialsTableField(targetField) && resetMaterialsAddedDateMap[targetId]) {
              tableResponse = resetMaterialsRowsAddedDate(tableResponse);
            }

            responseTargetMap[targetId] = tableResponse;
          } else {
            responseTargetMap[targetId] = response;
          }
        });
      }
    });
  });

  nativeFields.forEach(({ id, key }) => {
    if (id in fieldMappings) {
      const targetFieldIds = fieldMappings[id];
      targetFieldIds.forEach((targetId) => {
        responseTargetMap[targetId] = { value: nativeData[key] };
      });
    }
  });
  const responseSections = [];
  newTemplateSections.forEach((section) => {
    const { fields = [], name: sectionName, id: sectionId } = section;
    const fieldResponses = fields.map((field) => {
      const {
        id,
        selectedType,
        configProps: {
          title,
          dataType,
          subDataType,
          disablePrefillFromCard = false,
        } = {},
      } = field;
      const {
        [id]: mappingResponse,
      } = responseTargetMap;

      // Pre-populate dropdown from the source card
      let linkResponse;
      const shouldPrefillLinkedRecord = (
        profileType === 'card'
        && !disablePrefillFromCard
        && !mappingResponse
        && cardLink
        && !linkAssigned
        && selectedType === 'dropdown'
        && dataType === linkToDataType[linkType]
      );
      if (shouldPrefillLinkedRecord) {
        linkResponse = {
          values: [{ id: linkId, name: typeDataMap[linkType][linkId]?.name || linkId }],
        };
        linkAssigned = true;
      }

      const shouldPrefillSourceCard = (
        profileType === 'card'
        && selectedType === 'dropdown'
        && dataType === 'Cards'
        && subDataType === nativeData.boardId
        && profileId
        && !disablePrefillFromCard
      );
      if (shouldPrefillSourceCard) {
        linkResponse = {
          values: [{ id: profileId, name: nativeData.title }],
        };
      }

      const ourResponse = mappingResponse || linkResponse || {};

      if (selectedType === 'dropdown') {
        const { values = [] } = ourResponse;
        if (dataType === 'Customers') {
          values.forEach((val) => customerIdSet.add(val?.id ? val?.id : val));
        } else if (dataType === 'Projects') {
          values.forEach((val) => projectIdSet.add(val?.id ? val?.id : val));
        } else if (dataType === 'Vendors' && !formVendorId) {
          const [{ id: firstId } = {}] = values;
          formVendorId = firstId;
        } else if (dataType === 'Costcodes' && !formCostcodeId) {
          const [{ id: firstId, phaseId } = {}] = values;
          if (firstId) {
            formCostcodeId = `${phaseId ?? 'unphased'}.${firstId}`;
          }
        }
      }

      return {
        type: selectedType,
        title,
        fieldId: id,
        sectionId,
        response: ourResponse,
        duplicatedParentId: field.duplicatedParentId,
      };
    });

    responseSections.push({
      id: sectionId,
      name: sectionName,
      duplicatedParentId: section.duplicatedParentId,
      fields: fieldResponses,
    });
  });

  const projectIds = Array.from(projectIdSet);
  const customerIds = Array.from(customerIdSet);
  const newData = {
    formData: {
      name: templateName,
      sections: responseSections,
    },
    ids: {
      customerIds: customerIds?.length ? customerIds : undefined,
      projectIds: projectIds?.length ? projectIds : undefined,
      vendorId: formVendorId,
      costcodeId: formCostcodeId,
    },
  };
  return newData;
};

/**
 * Gets filters associated with form columns
 * @param {array} data
 * @param {object} idMap
 * @param {string} key
 * @returns {array}
 */
export const getFilters = ({ data = [], idMap, key, formatter }) => {
  const ids = [];

  data.forEach((td) => {
    const { [key]: val } = td;
    if (Array.isArray(val)) {
      ids.push(...val);
    } else {
      ids.push(val);
    }
  });

  const idSet = new Set(ids.filter(Boolean));
  return Array.from(idSet).map((id) => {
    const {
      [id]: entity = {},
    } = idMap;

    const { name = 'None' } = formatter ? formatter(entity) : entity;
    return { text: name, value: id };
  });
};

/**
 * Checks if a term is included in a set
 * @param {Set<string>} set
 * @param {string} term
 * @returns {boolean}
 */
export const setIncludesTerm = (set, term) => {
  const arr = Array.from(set);
  return arr.some((val) => includesTerm(val, term));
};

/**
 * Form on click handler for manual/assigned forms
 * @param {Function} dispatch
 * @param {boolean} loading
 * @param {Function} setLoading
 * @param {Function} setAssignedForm
 * @param {Function} setShowDetail
 * @param {Function} setSelectedIsEdit
 * @param {Function} setSelectedIsResubmit
 */
export const handleFormClick = async ({
  record,
  dispatch,
  loading,
  setLoading,
  setAssignedForm,
  setShowDetail,
  setSelectedIsEdit,
  setSelectedIsResubmit,
}) => {
  if (!dispatch || loading) return;
  setLoading(true);
  let prom;
  let newAssigned;
  if (record.templateId) {
    if (record.isDraft) {
      // Manual Draft
      newAssigned = { draftId: record.id };
      prom = dispatch(getTemplateDetails(
        record.templateId,
        record.id,
        {
          circularFieldIds: record?.data?.circularFieldIds,
        },
      ));
    } else {
      // Assigned form
      newAssigned = { id: record.id };
      prom = dispatch(getFormById(record.id));
    }
  } else {
    // Manual Form
    prom = dispatch(getTemplateDetails(
      record.id,
      undefined,
      {
        circularFieldIds: record?.data?.circularFieldIds,
      },
    ));
  }
  const fetched = await prom;
  if (fetched) {
    setAssignedForm?.(newAssigned);
    setShowDetail(true);
    setSelectedIsEdit?.(!!record.editable);
    const hasResubmitted = !!fetched.steps?.some((step) => step.name === 'resubmit');
    setSelectedIsResubmit?.(
      record.editable ? hasResubmitted : !!record.isResubmit,
    );
  }
  setLoading(false);
};

/**
 * Returns true if the distribution is a valid new distribution, false otherwise
 *    - amount must be > 0
 *    - costcode must have been selected/chosen
 * @param {*} distribution
 * @returns
 */
const isValidNewDistribution = (distribution = {}) => {
  const { isNew, amount, costcodeId } = distribution || {};
  return isNew && amount && costcodeId;
};

/**
 * Reformats/prepares attachment path
 * @param {string} path
 * @returns {string}
 */
const prepareAttachmentPath = (path) => (
  path === 'Files/'
    ? ''
    : removeTrailingSlash(path)
);

/**
 * Prepares/creates correctly formatted invoice payload
 * @param {string} type
 * @param {object} formInputs
 * @param {object} existingInvoice
 * @param {object} vendors
 * @param {array} statuses
 * @param {File | null} file
 * @returns {object} payload
 */
export const prepareInvoicePayload = ({
  type,
  formInputs,
  existingInvoice,
  vendors,
  statuses,
  file,
}) => {
  const {
    formId,
    invoiceNumber,
    invoiceAmount,
    invoiceDescription,
    invoiceNotes,
    invoiceIssueDate,
    invoiceDueDate,
    vendorId,
    vendor,
    statusId,
    status,
    filePath,
    projectId,
    costcodeDistributions: submittedCostcodeDistributions = [],
    purchaseAccount,
    purchaseAccountType,
    type: invoiceType,
    qboSyncPrevented,
    divisionId,
    taxCode,
    taxCalculation,
    taxPercentage,
  } = formInputs;

  // Convert moment to millis:
  const invoiceDateIssuedMillis = invoiceIssueDate ? moment(invoiceIssueDate).valueOf() : null;
  const invoiceDueDateMillis = invoiceDueDate ? moment(invoiceDueDate).valueOf() : null;

  const payload = {
    invoiceNumber,
    amount: invoiceAmount,
    description: invoiceDescription,
    notes: invoiceNotes,
    dateIssued: invoiceDateIssuedMillis,
    dueDate: invoiceDueDateMillis,
    purchaseAccount,
    purchaseAccountType,
    type: invoiceType,
    qboSyncPrevented,
    divisionId,
    taxCode,
    taxCalculation,
    taxPercentage,
  };

  if (formId) {
    payload.formId = formId;
  } else {
    payload.projectId = projectId;
  }

  // Add correct properties depending on payload type:
  if (type === INVOICE_DRAWER_ADD_MODE) {
    payload.filePath = prepareAttachmentPath(filePath);
    payload.files = file ? [file] : [];
    payload.costcodeDistributions = submittedCostcodeDistributions
      .filter(({ amount, costcodeId } = {}) => amount && costcodeId)
      .map((distribution) => {
        const {
          amount,
          costcodeId,
          phaseId,
          projectId,
          defaultCategoryId,
          customCategoryId,
        } = distribution || {};
        return {
          amount,
          costcodeId,
          phaseId: phaseId === 'unphased' ? null : phaseId,
          projectId,
          defaultCategoryId: defaultCategoryId || null,
          customCategoryId: customCategoryId || null,
        };
      });
  } else if (type === INVOICE_DRAWER_EDIT_MODE) {
    const {
      files: existingFiles = [],
      costcodeDistributions: existingCostcodeDistributions = [],
      taxPercentage: existingTaxPercentage,
    } = existingInvoice || {};

    // File Path Details:
    payload.prvFilePath = existingFiles && existingFiles[0] && typeof existingFiles[0].path === 'string' && existingFiles[0].path.length
      ? removeTrailingSlash(existingFiles[0].path)
      : '';
    payload.newFilePath = prepareAttachmentPath(filePath);
    // Files:
    const newFiles = file ? [file] : [];
    const existingFileSet = new Set(existingFiles.map((file) => file.id));
    const newFileSet = new Set(newFiles.filter((file) => file.id)
      .map((file) => file.id));
    const toDeleteSet = new Set(existingFiles
      .filter((file) => file.id && !newFileSet.has(file.id))
      .map((file) => file.id));
    payload.filesToDelete = existingFiles
      .filter((file) => file.id && !newFileSet.has(file.id));
    payload.filesToUpdate = newFiles
      .filter((file) => file.id && !toDeleteSet.has(file.id));
    payload.filesToAdd = newFiles
      .filter((file) => !file.id || !existingFileSet.has(file.id));

    // Costcode Distributions:
    const existingDistributionMap = getIdMap(existingCostcodeDistributions);
    const newDistributionIdSet = new Set(submittedCostcodeDistributions
      .map((distribution) => distribution.id));
    payload.costcodeDistributionsToUpdate = submittedCostcodeDistributions
      .filter(({
        isNew, id, amount: newAmt, costcodeId,
      }) => {
        const { amount: prvAmt } = existingDistributionMap[id] || {};
        return !isNew && newAmt && costcodeId && (prvAmt !== newAmt || existingTaxPercentage !== taxPercentage);
      })
      .map(({ id, amount }) => ({ id, amount }));
    payload.costcodeDistributionsToDelete = Object.keys(existingDistributionMap)
      .filter((distributionId) => !newDistributionIdSet.has(distributionId));
    payload.costcodeDistributionsToAdd = submittedCostcodeDistributions
      .filter((distribution) => isValidNewDistribution(distribution))
      .map((distribution) => {
        const {
          amount,
          costcodeId,
          phaseId,
          projectId,
          defaultCategoryId,
          customCategoryId,
        } = distribution || {};
        return {
          amount,
          costcodeId,
          phaseId: phaseId === 'unphased' ? null : phaseId,
          projectId,
          defaultCategoryId: defaultCategoryId || null,
          customCategoryId: customCategoryId || null,
        };
      });
  }

  // On existing vendor chosen:
  if (vendors[vendorId]) {
    payload.vendorId = vendorId;
  }

  // On new vendor create:
  if (vendor) {
    delete vendor.id;
    payload.vendor = decoratePayloadWithLabels(vendor);
  }

  // If status was chosen:
  if (statusId) {
    if (statuses.map(({ id }) => id).includes(statusId)) {
      // Pre-existing status:
      payload.statusId = statusId;
    } else {
      // Newly defined status:
      const { label } = status;
      payload.status = label;
    }
  }

  return payload;
};

/**
 * gets currency formatted number
 * @param {number} val
 * @returns {string} val in currency format
 */
export const getFormattedCurrency = (val) => (val ? currencyFormatter(val) : '$ 0');

/**
 * Gets a formatted date from millis
 * @param {number} date - millis
 * @returns {string}
 */
export const getFormattedDate = (date) => ((date && typeof date === 'number')
  ? `${DateTime.fromMillis(date).toLocaleString(DateTime.DATE_MED)}` : '-');

/**
 * Get invoice-costcode distribution key
 * @param {string | undefined} phaseId
 * @param {string} costcodeId
 * @returns {string} distribution key
 */
export const getInvoiceCostcodeDistributionKey = (phaseId, costcodeId) => `${phaseId}.${costcodeId}`;

/**
 * Gets the proper invoice drawer title depending on the mode
 * @param {object} invoice
 * @param {string} mode
 * @returns {string} invoice drawer title
 */
export const getInvoiceAddDrawerTitle = (invoice, mode) => {
  const { invoiceNumber } = invoice || {};
  switch (mode) {
    case INVOICE_DRAWER_ADD_MODE:
      return 'Add Invoice';
    case INVOICE_DRAWER_EDIT_MODE:
      return invoiceNumber ? `Edit Invoice: ${invoiceNumber}` : 'Edit Invoice';
    case INVOICE_DRAWER_VIEW_MODE:
      return invoiceNumber ? `Invoice: ${invoiceNumber}` : 'Invoice Details';
  }
};

/**
 * Retrieves a descriptive name representing a costcode selected in the TreeSelect
 * @param {string} projectId
 * @param {string} selectedId
 * @param {object} phaseMap
 * @param {object} costcodeMap
 * @returns {string} descriptive costcode name
 */
export const getDescriptivePhaseCostcodeName = ({
  projectId,
  selectedId = '',
  phaseMap = {},
  costcodeMap = {},
}) => {
  const [phaseId, costcodeId] = selectedId.split('.') || [];
  const {
    [costcodeId]: {
      name: costcodeName,
      code,
    } = {},
  } = costcodeMap;
  const {
    [phaseId]: { name: phaseName = projectId ? 'Unphased' : 'Global' } = {},
  } = phaseMap;
  return `${phaseName} - ${code} ${costcodeName}`;
};

/**
 * Gets the tree option title
 * @param {string} phaseName
 * @param {string} code
 * @param {string} costcodeName
 */
const getTreeOptionTitle = ({
  phaseName,
  code = '',
  costcodeName = '',
}) => `${phaseName} - ${code} ${costcodeName}`;

/**
 * Retrieves phased/unphased costcodes in a tree-structure
 * @param {string} projectId
 * @param {string} phaseId
 * @param {string[]} phaseIds
 * @param {array} phases
 * @param {array} costcodes
 * @param {object} costcodeMap
 * @param {object} projectMap
 * @param {boolean} disableOptions
 * @param {Set<string>} disabledKeys
 * @param {boolean} includeGlobal // global costcode selection setting
 * @param {object} costcodeCodeMap
 * @param {boolean} isClockIn // filter out non-clock costcodes
 * @returns {array} costcode/phases tree
 */
export const getPhaseCostcodeTreeData = ({
  projectId,
  phaseId,
  phaseIds,
  phases = [],
  costcodes = [],
  costcodeMap = {},
  projectMap = {},
  disableOptions = false,
  disabledKeys,
  includeGlobal = false,
  costcodeCodeMap,
  isClockIn = false,
}) => {
  const isLeafDisabled = (value) => disableOptions || !!disabledKeys?.has(value);
  const phaseIdsSet = phaseIds?.length ? new Set(phaseIds) : null;
  const hasSpecificPhaseFilter = !!phaseIdsSet?.size
    || (!!phaseId && phaseId !== 'Unphased');
  const isUnphasedFilter = phaseId === 'Unphased' && !phaseIdsSet?.size;

  const matchesPhase = (pId) => {
    if (isUnphasedFilter) return false;
    if (phaseIdsSet?.size) return phaseIdsSet.has(pId);
    if (phaseId && phaseId !== 'Unphased') return phaseId === pId;
    return true;
  };

  const uniqueUnphasedCostcodes = new Set();
  const valueKey = projectId ?? 'global';
  const unphasedCostcodes = {
    value: `${valueKey}-unphased`,
    title: projectId ? 'Unphased' : 'Global',
    selectable: false,
    children: [],
  };
  /*
    globalCostcodes is only used when includeGlobal is passed, otherwise
    global costcodes are parsed in unphasedCostcodes object
  */
  const globalCostcodes = {
    value: 'global-unphased',
    title: 'Global',
    selectable: false,
    children: [],
  };

  const phasedCostcodesAdded = {};
  const phasedCostcodes = {};
  const addedPhasedCodes = new Set();

  const hasPhasedCode = (code) => {
    if (phaseIdsSet?.size) {
      let found = false;
      phaseIdsSet.forEach((pid) => {
        if (addedPhasedCodes.has(`${pid}.${code}`)) found = true;
      });
      return found;
    }
    if (phaseId) return addedPhasedCodes.has(`${phaseId}.${code}`);
    return false;
  };

  // Add Associated Phased Costcodes, only if there is a project selected:
  if (projectId) {
    phases.forEach((phase) => {
      const {
        id: pId,
        name: phaseName,
        costcodeId,
        projectId: phaseProjectId,
      } = phase || {};
      if (!costcodeId
        || !pId
        || projectId !== phaseProjectId
        || !matchesPhase(pId)
        || phasedCostcodesAdded[pId]?.has(costcodeId)
      ) {
        return;
      }
      const projectName = projectMap[phaseProjectId] ? projectMap[phaseProjectId].name : '';
      if (!phasedCostcodes[pId]) {
        const title = projectName ? `${projectName} - ${phaseName}` : phaseName;
        phasedCostcodes[pId] = {
          value: pId,
          title,
          selectable: false,
          children: [],
        };
        phasedCostcodesAdded[pId] = new Set();
      }
      const costcode = costcodeMap[costcodeId] || {};
      const { name: costcodeName, code, canClockIn } = costcode;
      if (
        !costcodeName
        || !code
        || (!canClockIn && isClockIn)
      ) return;
      const phasedValue = `${pId}.${costcodeId}`;
      phasedCostcodes[pId].children.push({
        value: phasedValue,
        title: getTreeOptionTitle({
          costcodeName, code, phaseName,
        }),
        isLeaf: true,
        disabled: isLeafDisabled(phasedValue),
      });
      phasedCostcodesAdded[pId].add(costcodeId);
      addedPhasedCodes.add(`${pId}.${code}`);
    });
  }

  // Add Unphased/Global Costcodes:
  costcodes.forEach((costcode) => {
    const {
      id: costcodeId,
      name: costcodeName,
      code,
      active,
      projectId: costcodeProjectId,
      phased,
      canClockIn,
    } = costcode || {};

    const isGlobalCostcode = !costcodeProjectId;

    if (
      (hasSpecificPhaseFilter && !isGlobalCostcode)
      || (phased && !includeGlobal)
      || (phased && costcodeProjectId)
      || (!canClockIn && isClockIn)
    ) return;
    if (!active || uniqueUnphasedCostcodes.has(costcodeId)) return;

    /*
      Global costcode selection setting filtering
      1. filter global if phase is unphased and project specific costcode exists
      2. filter global that are unphased in project if phase exists
      3. filter global costcodes that exist in selected phase
      4. filter phased project costcodes if phase is unphased
    */

    const hasMatchingProjectSpecific = costcodeCodeMap?.[code] ?? null;

    if ((!projectId && isGlobalCostcode && !includeGlobal)
      || (projectId && projectId === costcodeProjectId)
    ) {
      if (hasMatchingProjectSpecific && isUnphasedFilter && hasMatchingProjectSpecific.phased) return;
      const unphasedValue = `unphased.${costcodeId}`;
      unphasedCostcodes.children.push({
        value: unphasedValue,
        title: getTreeOptionTitle({
          costcodeName,
          code,
          phaseName: projectId ? 'Unphased' : 'Global',
        }),
        isLeaf: true,
        disabled: isLeafDisabled(unphasedValue),
      });
      uniqueUnphasedCostcodes.add(costcodeId);
    }

    // includeGlobal block
    if (includeGlobal && isGlobalCostcode) {
      if (hasMatchingProjectSpecific
        && (
          isUnphasedFilter
          || (hasSpecificPhaseFilter && !hasMatchingProjectSpecific?.phased)
          || hasPhasedCode(code)
        )
      ) return;
      const globalValue = `global.${costcodeId}`;
      globalCostcodes.children.push({
        value: globalValue,
        title: getTreeOptionTitle({
          costcodeName,
          code,
          phaseName: 'Global',
        }),
        isLeaf: true,
        disabled: isLeafDisabled(globalValue),
      });
    }
  });
  unphasedCostcodes.children.sort(sortByObjProperty('title'));
  globalCostcodes.children.sort(sortByObjProperty('title'));

  // Remove phases that do not have any costcodes assigned:
  const refinedPhases = Object.values(phasedCostcodes)
    .filter((phase) => (
      phase && phase.children && Array.isArray(phase.children) && phase.children.length > 0
    ))
    .map((phase) => {
      const { children = [] } = phase;
      return {
        ...phase,
        children: children.sort(sortByObjProperty('title')),
      };
    })
    .sort(sortByObjProperty('title'));

  const refinedUnphased = includeGlobal && !unphasedCostcodes?.children?.length
    ? []
    : [unphasedCostcodes];

  let allCodes;
  if (hasSpecificPhaseFilter) {
    allCodes = refinedPhases;
  } else if (isUnphasedFilter) {
    allCodes = refinedUnphased;
  } else {
    allCodes = refinedUnphased.concat(refinedPhases);
  }

  if (includeGlobal) {
    allCodes = allCodes.concat(globalCostcodes);
  }

  return allCodes;
};

/**
 * Gets the phase-costcode key
 * @param {string | undefined} phaseId
 * @param {string} costcodeId
 * @returns {string} phase-costcode key
 */
export const getPhaseCostcodeKey = (phaseId, costcodeId) => (phaseId ? `${phaseId}.${costcodeId}` : `unphased.${costcodeId}`);

/**
 * Map form config to response
 * @param {object} templateSchema JSON of form template
 * @param {string} templateSchema.name Name of template
 * @param {array} templateSchema.sections Form template sections
 * @returns {object} Template schema mapped to the form response schema
 */
const convertFormConfigToResponse = (templateSchema) => {
  if (!templateSchema) return {};
  const sections = templateSchema.sections ?? [];
  return {
    name: templateSchema.name,
    sections: sections.map((section) => {
      const safeSection = section ?? {};
      const fields = safeSection.fields ?? [];
      return {
        ...safeSection,
        fields: fields.map((field) => {
          const responseField = {
            fieldId: field.id,
            sectionId: safeSection.id,
            title: field?.configProps?.title,
            type: field.selectedType,
          };
          if (field.selectedType === 'staticAttachments') {
            responseField.response = {
              fileIds: field?.configProps?.files?.map?.((file) => file.id)
                .filter((fileId) => !!fileId) ?? [],
            };
          }
          return responseField;
        }),
      };
    }),
  };
};

const decorateFormDataWithConfig = (
  formData = [],
  { sections: templateSections = [] } = {},
) => {
  const templateFieldMap = templateSections?.reduce((acc, { fields = [] }) => {
    fields?.forEach((field) => {
      const { id } = field ?? {};
      acc[id] = field;
    });
    return acc;
  }, {}) ?? {};
  return formData.map((section) => {
    const { id: sectionId, fields } = section ?? {};
    const safeFields = fields ?? [];
    const [firstField] = safeFields;
    const realSectionId = firstField?.sectionId ?? sectionId;
    return {
      ...section,
      id: realSectionId,
      fields: safeFields?.map((field) => {
        if (field?.selectedType !== 'multiSig') return field;
        const {
          [field.fieldId]: {
            configProps,
          } = {},
        } = templateFieldMap;
        if (!configProps) return field;
        return {
          ...field,
          configProps,
        };
      }) ?? [],
    };
  });
};

/**
 * Converts a completed form into a PDF
 * @param {number} useStandardTemplate
 * @param {object[]} formData
 * @param {object} fileMap
 * @param {object} data
 * @param {number} createdAt
 * @param {number} submittedAt
 * @param {array} drawOptions
 * @param {string} title
 * @param {string} submitterName
 * @param {string} employeeId
 * @param {*} employeeSignature
 * @param {object} collected
 * @param {object} settings
 * @param {File} logo
 * @param {number} number
 * @returns {Promise<File>} completed form PDF
 */
export const constructCompletedFormPDF = async (params) => {
  const {
    employeeSignature,
    useStandardTemplate,
    pdfVersion,
    formData,
    fileMap,
    data,
    createdAt,
    submittedAt,
    drawOptions,
    title,
    submitterName,
    employeeId,
    collected,
    settings,
    logo,
    number,
    shouldReturnPDF = false,
    additionalCollectedHeaderItems = [],
    templateSchema = {},
    projectIdMap = {},
    equipmentIdMap = {},
  } = params || {};
  const files = [];
  const staticProms = Object.values(fileMap).map(async (file) => {
    const { id: fileId, name, jsFileObject } = file;
    if (!jsFileObject || !(jsFileObject instanceof File)) return Promise.resolve();
    files.push({
      id: fileId,
      name,
      data: await jsFileObject.arrayBuffer(),
    });
  });

  if (logo) {
    files.push({
      id: 'logo',
      name: logo.name,
      data: await logo.arrayBuffer(),
    });
  }

  await Promise.all(staticProms);

  // Need to load files from config
  // for static attachment fields
  const fullData = data ?? convertFormConfigToResponse(templateSchema);

  const completedForm = {
    ...fullData,
    collected: {
      ...collected,
      number: number ? number.toString() : number,
      employeeSignature: {
        collect: collected.employeeSignature,
      },
    },
  };

  const rawPDFData = await formToPDF({
    sections: decorateFormDataWithConfig(formData, templateSchema),
    useStandardTemplate,
    pdfVersion,
    name: title,
    collected: templateSchema?.collected,
  }, {
    employeeName: submitterName,
    employeeId,
    timestamp: submittedAt ?? createdAt,
    completedForm,
    files,
    drawOptions,
    signatureData: employeeSignature ? await employeeSignature.arrayBuffer() : null,
    settings,
    projectIdMap,
    equipmentIdMap,
    shouldReturnPDF,
    additionalCollectedHeaderItems,
    fileMap,
    isWeb: true,
  });

  return shouldReturnPDF
    ? rawPDFData
    : new File([rawPDFData], `${title}.pdf`, { type: 'application/pdf' });
};

/**
 * Extracts form fields from form sections and constructs options that can be used for antd Select
 * @param {Set} types - input field types to include
 * @param {array} sections - form sections
 * @returns {array} antd select options
 */
export const extractFormFieldOptions = ({
  types = new Set(),
  sections = [],
}) => {
  const selectOptions = [{ value: null, label: 'None' }];
  sections.forEach(({ name: sectionName, fields = [] }) => {
    fields.forEach(({ id: fieldId, selectedType = '', configProps = {} }) => {
      const { title: fieldName } = configProps;
      if (types.has(selectedType)) {
        selectOptions.push({
          value: fieldId,
          label: `Section: ${sectionName} --- Field: ${fieldName}`,
        });
      }
    });
  });
  return selectOptions;
};

export const fieldTypesWithAPI = new Set(['table', 'multiSig', 'attribute']);

export const removeAPIFieldsFromSections = (sections = []) => {
  const newSections = [];
  sections.forEach(({ fields = [], ...rest }) => {
    const newFields = [];
    fields.forEach((field) => {
      const {
        selectedType,
        configProps = {},
      } = field;

      const {
        dataType,
        fieldTrigger,
      } = configProps;

      if (fieldTypesWithAPI.has(selectedType) && dataType !== 'Custom') return;
      if (selectedType === 'dropdown' && !dataTypesAllowedInExternalForm.has(dataType)) return;

      // remove field trigger config props
      const newField = { ...field };
      if (fieldTrigger) {
        const newConfigProps = { ...configProps };
        delete newConfigProps.fieldTrigger;
        delete newConfigProps.fieldTriggerProps;
        newField.configProps = newConfigProps;
      }

      newFields.push(newField);
    });
    newSections.push({ ...rest, fields: newFields });
  });
  return newSections;
};

export const isMultiDivisionForm = (divisionIds) => divisionIds?.length > 1;

export const getAllowedDivisionIds = ({
  userDivisions = {},
  userId,
  formDivisionIds = [],
}) => {
  const formDivSet = new Set(formDivisionIds);

  if (userId) {
    const userDivs = userDivisions[userId] ?? [];
    if (!formDivisionIds?.length) return userDivs;
    return userDivs.filter((divId) => formDivSet.has(divId));
  }

  return formDivisionIds || [];
};

export const resolveFormDivisionIds = ({ divisionIds, divisionId }) => {
  if (divisionIds?.length) return divisionIds.map((division) => division?.divisionId ?? division?.id ?? division)?.filter(Boolean);
  if (divisionId) return [divisionId];
  return [];
};

export const getProjectIdsForDivisions = (projects = [], divisionIds = []) => {
  const divisionSet = new Set((divisionIds || []).filter(Boolean));
  if (!divisionSet.size) return [];
  return projects
    .filter((project) => divisionSet.has(project.divisionId))
    .map((project) => project.id);
};

export const isInvalidMultiDivisionField = (field) => {
  const {
    selectedType,
    configProps = {},
  } = field;

  const {
    dataType,
    fieldTrigger,
  } = configProps;

  if (fieldTrigger) return true;
  if (selectedType === 'timeTrackingGrid') return true;
  if (selectedType === 'table' && !dataTypesAllowedInMultiDivisionTable.has(dataType)) return true;
  if (selectedType === 'dropdown' && !dataTypesAllowedInMultiDivisionDropdown.has(dataType)) return true;
  if (selectedType === 'dynamicAttribute' && dataType && !isAllowedDynamicAttributeType(dataType)) return true;
  return false;
};

export const getInvalidMultiDivisionFields = (sections = []) => {
  const invalidFields = [];
  sections.forEach(({ fields = [], name: sectionName }) => {
    fields.forEach((field) => {
      if (isInvalidMultiDivisionField(field)) {
        invalidFields.push({ ...field, sectionName });
      }
    });
  });
  return invalidFields;
};

export const getInvalidMultiDivisionWorkflowNodes = (workflow = {}) => {
  const { nodes = [] } = workflow;
  return nodes.filter(({ type }) => (
    !MULTI_DIVISION_ALLOWED_WORKFLOW_ACTION_TYPES.has(type)
    && !MULTI_DIVISION_ALLOWED_WORKFLOW_INFRASTRUCTURE_TYPES.has(type)
  ));
};

export const preparePDFFieldsFromSections = ({ sections = [], isExternalForm }) => {
  const newSections = [];
  sections.forEach(({ fields = [], ...rest }) => {
    const newFields = [];
    fields.forEach((field) => {
      const {
        selectedType,
        configProps = {},
      } = field;

      const {
        dataType,
        fieldTrigger,
      } = configProps;

      if (isExternalForm && fieldTypesWithAPI.has(selectedType) && dataType !== 'Custom') return;
      if (isExternalForm && selectedType === 'dropdown' && !dataTypesAllowedInExternalForm.has(dataType)) return;

      const newField = { ...field };
      const newConfigProps = { ...configProps };

      switch (selectedType) {
        case 'table': {
          const { columns = [] } = configProps;
          const newColumns = columns.filter(({ isHideInPDF }) => !isHideInPDF);
          newConfigProps.columns = newColumns;
          break;
        }
        default:
          break;
      }

      // remove field trigger config props
      if (fieldTrigger) {
        delete newConfigProps.fieldTrigger;
        delete newConfigProps.fieldTriggerProps;
        newField.configProps = newConfigProps;
      }

      newFields.push(newField);
    });
    newSections.push({ ...rest, fields: newFields });
  });
  return newSections;
};

export const getPDF = async ({
  form,
  drawOptions,
  fileMap,
  settings,
  options = {},
}) => {
  const files = await Promise.all(
    Object.values(fileMap).map(async ({ id, name, jsFileObject }) => {
      let data;
      try {
        data = await jsFileObject.arrayBuffer();
      } catch (error) {
        return null;
      }
      return {
        id,
        name,
        data,
      };
    }),
  );
  const pdfData = await formToPDF(form, {
    drawOptions,
    files: files.filter(Boolean),
    settings,
    fileMap,
    isWeb: true,
    ...options,
  });
  const file = new File([pdfData], 'file.pdf', { type: 'application/pdf' });
  return file;
};

export const getStaticAttachmentFiles = ({
  sections,
  files = [],
}) => {
  if (!sections) return { idMap: {}, files };
  const fullFiles = [...files];
  const idMap = {};
  sections.forEach((section) => {
    const { fields = [] } = section ?? {};
    fields?.forEach?.((field) => {
      if (field.selectedType !== 'staticAttachments') return;
      const {
        configProps: {
          files: fieldFiles = [],
        } = {},
        id: fieldId,
      } = field;
      fieldFiles.forEach((file, fileIndex) => {
        if (!file.existing && !file.jsFileObject?.existing) {
          const realId = `${fieldId}-${fileIndex}`;
          idMap[realId] = fullFiles.length;
          fullFiles.push(file);
        }
      });
    });
  });
  return { idMap, files: fullFiles };
};

/**
   * Filters non text type custom fields and formats for use
   * with attributes
   * @param {array} fields
   * @returns {array}
   */
const getTextCustomFields = (fields) => (
  fields.filter((field) => field.type === 'text').map((field) => ({
    attr: field.v1ID,
    name: field.title,
    type: 'text',
  }))
);

/**
   * Retrieves the attribute map with custom fields for:
   * Costcode custom fields
   * User custom fields
   * @param {object} attributeMap - base attribute map
   * @param {object} costcodeCustomFields
   * @param {object} userCustomFields
   * @returns {object}
   */
export const getAttributeMapWithCustomFields = ({
  attributeMap,
  costcodeCustomFields,
  userCustomFields,
  bucketTemplates = [],
}) => {
  const { fields: costcodeFields = [] } = costcodeCustomFields;
  const { fields: userFields = [] } = userCustomFields;

  const textCostcodeFields = getTextCustomFields(costcodeFields);
  const textUserFields = getTextCustomFields(userFields);

  const newAttributeMap = { ...attributeMap };
  const {
    Costcodes: {
      fields: ccMapFields = [],
    } = {},
    User: {
      fields: userMapFields = [],
    },
  } = newAttributeMap;

  const newCostcodeMapFields = ccMapFields.concat(textCostcodeFields);
  const newUserMapFields = userMapFields.concat(textUserFields);
  newAttributeMap.Costcodes.fields = newCostcodeMapFields;
  newAttributeMap.User.fields = newUserMapFields;

  bucketTemplates?.forEach(({ id, customFields: { fields = [] } = {} }) => {
    const textFields = getTextCustomFields(fields);
    const newTemplateMapFields = (newAttributeMap[id]?.fields || []).concat(textFields);
    newAttributeMap[id] = {
      fields: newTemplateMapFields,
    };
  });

  return newAttributeMap;
};

/**
 * Checks whether the user, their role, or one of their teams has permission to view a section.
 * User-specific permission settings are prioritized over others
 * @param {object} user - The user being checked (needs id, position and teams)
 * @param {object} positionNameMap - map of positionName to position details
 * @param {object} permissions - map of permission id to permission details
 * @param {string} type - permission being checked (canView or canEdit)
 * @returns {boolean} - if the user has permission
 */
const hasSectionPermissions = ({
  user,
  positionNameMap,
  permissions,
  type,
}) => {
  const userPositionId = positionNameMap[user.position]?.id;
  const userTeams = user.teams?.map((team) => team.id);

  // Empty or not set acts as full permissions
  if (!permissions || !Object.values(permissions)?.length) return true;

  // Check User permission
  if (permissions[user.id]) return permissions[user.id]?.[type];
  // Check Role permission
  if (permissions[userPositionId]?.[type]) return true;
  // Check Team permissions
  return userTeams?.some((teamId) => permissions[teamId]?.[type]);
};

export const getSectionPermissionMap = ({
  sections,
  users,
  positionNames,
  userId,
}) => {
  const positionNameMap = getIdMap(positionNames, 'name');
  const userMap = getIdMap(users);
  const {
    [userId]: user = {},
  } = userMap;
  const sectionPermissionMap = {};
  sections.forEach(({ id, settings }) => {
    sectionPermissionMap[id] = {
      canView: hasSectionPermissions({
        user,
        positionNameMap,
        permissions: settings?.permissions,
        type: 'canView',
      }),
      canEdit: hasSectionPermissions({
        user,
        positionNameMap,
        permissions: settings?.permissions,
        type: 'canEdit',
      }),
    };
  });
  return sectionPermissionMap;
};

export const textBasedFieldTypes = new Set([
  'text',
  'yes-no',
  'calculation',
  'dropdown',
  'dateRange',
  'dateTime',
  'attribute',
]);

export const simpleTextBasedFieldTypes = new Set([
  'text',
  'calculation',
  'attribute',
]);

export const getLinkId = ({
  responses = {},
  defaultValue,
  linkField,
  allowMultiple,
}) => {
  if (!linkField) return defaultValue;
  const {
    [linkField]: {
      values = [],
      value,
    } = {},
  } = responses;
  if (linkField === 'project' || linkField === 'costcode') {
    /*
      Timecard custom field attribute can link
      to timecard project/costcode which only returns a single value

      https://ontraccr.sentry.io/issues/5685831755/?project=5891694
    */
    return value;
  }
  if (allowMultiple) return values?.map?.((v) => v.id) ?? [];
  const [{ id: linkId } = {}] = values ?? [];
  return linkId;
};

export const getLinkedProjectIdForCostCodes = ({
  linkProjectCostCodes,
  linkProjectCostCodesDropdown,
  responses,
}) => {
  if (!linkProjectCostCodes || !linkProjectCostCodesDropdown) return null;
  return getLinkId({
    responses,
    defaultValue: null,
    linkField: linkProjectCostCodesDropdown,
  });
};

export const getScopedTableCostcodes = ({
  costcodes = [],
  linkProjectCostCodes,
  linkProjectCostCodesDropdown,
  responses,
}) => {
  const linkedProjectId = getLinkedProjectIdForCostCodes({
    linkProjectCostCodes,
    linkProjectCostCodesDropdown,
    responses,
  });

  if (linkProjectCostCodes && linkedProjectId) {
    return {
      costcodes: costcodes.filter((cc) => cc.projectId === linkedProjectId),
      linkedProjectId,
    };
  }

  return {
    costcodes: costcodes.filter((cc) => !cc.projectId),
    linkedProjectId: null,
  };
};

export const getFormOptions = ({
  formTemplates = {},
  selectedDivisions = new Set(),
  projectIdMap = {},
}) => {
  const formList = Object.values(formTemplates);
  formList.sort(sortByString('name'));
  return formList
    .filter((formTemplate) => formTemplate.active && selectedDivisions.has(formTemplate.divisionId))
    .map((formTemplate) => {
      const {
        projectId,
      } = formTemplate;
      const {
        [projectId]: {
          name: projectName,
        } = {},
      } = projectIdMap;
      const label = projectName ? `${formTemplate.name} - ${projectName}` : formTemplate.name;
      return { value: formTemplate.id, label };
    });
};

export const getCustomFieldTableColumns = (sections = []) => {
  const columns = [];

  sections?.forEach(({ fields = [] }) => {
    fields?.forEach((field) => {
      const {
        type,
        configProps: {
          title,
        } = {},
      } = field;

      if (textBasedFieldTypes.has(type)) {
        columns.push({
          title,
          dataIndex: field.id,
          key: field.id,
        });
      }
    });
  });

  return columns;
};

export const getFieldMap = (sections = [], idKey = 'fieldId') => {
  const fieldMap = {};
  sections.forEach(({ fields = [] }) => {
    fields.forEach((field) => {
      fieldMap[field[idKey]] = field;
    });
  });
  return fieldMap;
};

export const getSnapshotFormData = ({
  snapshot,
  secondarySnapshot = null,
  setSelectedFile,
  setSelectedFileDetails,
}) => {
  const {
    sections = [],
    templateSchema: {
      sections: templateSections = [],
    } = {},
    fileMap = {},
  } = snapshot;
  const templateMap = {};
  templateSections.forEach(({ id: sectionId, fields = [], settings }) => {
    if (!(sectionId in templateMap)) {
      templateMap[sectionId] = {
        settings,
      };
    }
    const subMap = templateMap[sectionId];
    fields.forEach((field) => {
      const { id: fieldId } = field;
      subMap[fieldId] = field;
    });
  });

  const secondarySnapshotFieldMap = getFieldMap(secondarySnapshot?.sections);

  return parseCompletedForm({
    sections,
    fileMap,
    templateMap,
    secondaryFormFieldMap: secondarySnapshot ? secondarySnapshotFieldMap : null,
    setSelectedFile,
    setSelectedFileDetails,
  });
};

export const findSnapshotsForRange = ({
  snapshots,
  range,
}) => {
  if (!range?.length) return {};
  const [
    start,
    end,
  ] = range;

  let firstSnapshot;
  let secondSnapshot;

  // snapshots are ordered from most recent to oldest
  // we want to find the snapshots that are closest to the start and end dates in range
  for (let i = 0; i < snapshots.length; i += 1) {
    const snapshot = snapshots[i];
    const snapshotDate = moment.unix(snapshot.timestamp / 1000);

    // Want to find the second snapshot that is before the end date
    // This is the final change to the form
    if (
      snapshotDate.isSameOrBefore(end.endOf('day'))
      && snapshotDate.isSameOrAfter(start.startOf('day'))
      && !secondSnapshot
    ) {
      secondSnapshot = snapshot;
    }

    // Want to find the first snapshot that is before the start date
    // This is the original form before any changes in date range
    if (snapshotDate.isSameOrBefore(start)) {
      firstSnapshot = snapshot;
      break;
    }
  }
  return {
    firstSnapshot,
    secondSnapshot,
  };
};

export const getRelevantSnapshotData = ({
  formData,
  range,
  snapshots,
  setSelectedFile,
  setSelectedFileDetails,
}) => {
  if (range?.length !== 2) return formData;

  const {
    firstSnapshot,
    secondSnapshot,
  } = findSnapshotsForRange({
    snapshots,
    range,
  });

  // If there is no snapshot in range, we don't want to show any form data
  if (!secondSnapshot) return null;
  return getSnapshotFormData({
    snapshot: secondSnapshot,
    secondarySnapshot: firstSnapshot,
    setSelectedFile,
    setSelectedFileDetails,
  });
};

/**
 * Parses the 'filtered' selected values into the actual form response for certain fields.
 * Used for mapping filtered snapshot data into a form
 */
export const parseSelectedIntoResponse = ({
  type,
  response,
}) => {
  const newResponse = {
    ...response,
  };

  switch (type) {
    case 'yes-no': {
      if (newResponse.selected === null) {
        newResponse.value = undefined;
      }
      return newResponse;
    }
    case 'dropdown': {
      newResponse.values = newResponse?.selected ?? [];
      return newResponse;
    }
    case 'attachment': {
      const { files } = newResponse;
      const filteredFileIds = files.map((file) => file.id);
      newResponse.fileIds = filteredFileIds;
      return newResponse;
    }
    default: {
      return newResponse;
    }
  }
};

export const parseFilteredForm = (formData) => (
  formData.map((section) => ({
    ...section,
    fields: section.fields.map((field) => ({
      ...field,
      response: parseSelectedIntoResponse({
        type: field.type,
        response: field.response,
      }),
    })),
  }))
);

const variableRegex = /(\[(?:\w|\W|\/|-|:| )+?\])/g;
export const formatVariableText = ({
  text,
  variableMap,
}) => {
  if (!text) return '';
  const splitArr = text.split(variableRegex);
  return splitArr.reduce((acc, word) => {
    const match = word.match(variableRegex);
    if (match !== null) {
      const { 0: fullMatch } = match;
      const tagText = fullMatch.slice(1, fullMatch.length - 1);
      if (!(tagText in variableMap)) return acc + word;
      return acc + variableMap[tagText];
    }
    return acc + word;
  }, '');
};

export const fillFormVariableText = ({
  templateSections = [],
  responseSections = [],
  variableMap,
  text,
}) => {
  const newVariableMap = {
    ...variableMap,
  };

  const fieldMap = {};
  templateSections.forEach(({ fields = [] }) => {
    fields.forEach((field) => {
      const { id: fieldId } = field;
      fieldMap[fieldId] = field;
    });
  });

  responseSections?.forEach(({ name, fields = [] }) => {
    fields?.forEach(({
      fieldId,
      title,
      response: {
        date: responseDate,
        time: responseTime,
        timezone,
        value,
        values,
      } = {},
      type: fieldType,
    }) => {
      if (fieldType === 'text' || fieldType === 'attribute') {
        newVariableMap[`${name} - ${title}`] = value || '';
      } else if (fieldType === 'staticText') {
        newVariableMap[`${name} - ${title}`] = value || title || '';
      } else if (fieldType === 'dropdown') {
        newVariableMap[`${name} - ${title}`] = values
          ?.map((option) => option?.name)
          ?.filter(Boolean)
          .join(', ') || '';
      } else if (fieldType === 'dateTime') {
        const {
          configProps: {
            hideDate,
            hideTime,
            use24HourTime,
          } = {},
        } = fieldMap[fieldId] ?? {};

        const formattedDate = hideDate || !responseDate ? '' : DateTime.fromMillis(responseDate).setZone(timezone).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
        let formattedTime = '';

        if (!hideTime && responseTime) {
          formattedTime = use24HourTime
            ? DateTime
              .fromMillis(responseTime)
              .setZone(timezone)
              .toFormat('HH:mm')
            : DateTime
              .fromMillis(responseTime)
              .setZone(timezone)
              .toLocaleString(DateTime.TIME_SIMPLE);
        }
        const connector = formattedDate && formattedTime ? ' ' : '';
        newVariableMap[`${name} - ${title}`] = `${formattedDate}${connector}${formattedTime}`;
      }
    });
  });
  return formatVariableText({ text, variableMap: newVariableMap });
};

export const formatFormDropdownList = ({
  formList = [],
  projectIdMap = {},
  vendorIdMap = {},
  projectId,
  shouldFilterByProjects = false,
}) => {
  let filteredList = formList;

  if (shouldFilterByProjects) {
    filteredList = formList.filter((form) => (
      !projectId || form.projects?.includes(projectId)
    ));
  }

  return filteredList
    .map((form) => {
      const {
        id,
        lastUpdated,
        status,
        projects,
        vendors,
        preloadedInto = [],
      } = form;

      const preloadedIntoForms = preloadedInto.map((form) => FormHelpers.formatFormName(form));

      return {
        id,
        name: FormHelpers.formatFormName(form),
        subNames: [
          lastUpdated ? DateTime.fromMillis(lastUpdated).toLocaleString(DateTime.DATETIME_MED) : '',
          status ?? '',
          projects ? projects.map((formProjectId) => projectIdMap[formProjectId]?.name).join(', ') : '',
          vendors ? vendors.map((formVendorId) => vendorIdMap[formVendorId]?.name).join(', ') : '',
          preloadedIntoForms.length ? `Preloaded into: ${preloadedIntoForms.join(', ')}` : '',
        ],
      };
    });
};

const getSectionPermissionId = ({ id: sectionId, duplicatedParentId }) => (
  duplicatedParentId ?? sectionId
);

export const isHiddenSectionStaticTextField = (
  section,
  field,
  sectionPermissionMap,
) => {
  if (!sectionPermissionMap) return false;
  const { selectedType, configProps = {} } = field;
  const permissionSectionId = getSectionPermissionId(section);
  return (
    !sectionPermissionMap[permissionSectionId]?.canView
    && selectedType === 'staticText'
    && configProps.title !== undefined
  );
};

const isSectionConditionallyHidden = (responses, section) => {
  const {
    settings: {
      hasConditionalRendering: sectionHasConditionalRendering,
      conditionalRenderingFormula: sectionConditionalRenderingFormula,
    } = {},
  } = section;
  return (
    sectionHasConditionalRendering
    && FormHelpers.isConditionalRenderingFormulaComplete(sectionConditionalRenderingFormula)
    && !FormHelpers.executeConditionalRenderingFormula({
      formula: sectionConditionalRenderingFormula,
      responses,
    })
  );
};

const isFieldConditionallyHidden = (responses, field) => {
  const { configProps = {} } = field;
  const { hasConditionalRendering, conditionalRenderingFormula } = configProps;
  return (
    hasConditionalRendering
    && FormHelpers.isConditionalRenderingFormulaComplete(conditionalRenderingFormula)
    && !FormHelpers.executeConditionalRenderingFormula({
      formula: conditionalRenderingFormula,
      responses,
    })
  );
};

const isSectionHiddenByPermissions = (section, sectionPermissionMap) => {
  if (!sectionPermissionMap) return false;
  const permissionSectionId = getSectionPermissionId(section);
  return !sectionPermissionMap[permissionSectionId]?.canView;
};

export const filterSectionsHiddenFromViewer = (sections = [], sectionPermissionMap) => {
  if (!Array.isArray(sections)) return sections;
  if (!Object.keys(sectionPermissionMap || {}).length) return sections;

  return sections.filter((section) => {
    const permissionSectionId = getSectionPermissionId(section) ?? section.fields?.[0]?.sectionId;
    return !permissionSectionId || sectionPermissionMap[permissionSectionId]?.canView;
  });
};

export const syncHiddenSectionStaticTextResponses = ({
  responses,
  sections = [],
  sectionPermissionMap,
}) => {
  if (!sectionPermissionMap) {
    return { responses, didResponseChange: false };
  }

  const newResponses = { ...responses };
  let didResponseChange = false;

  sections.forEach((section) => {
    if (!isSectionHiddenByPermissions(section, sectionPermissionMap)) return;
    if (isSectionConditionallyHidden(newResponses, section)) return;

    const { fields = [] } = section;
    fields.forEach((field) => {
      const { id: fieldId, configProps = {}, selectedType } = field;
      if (selectedType !== 'staticText' || configProps.title === undefined) return;
      if (isFieldConditionallyHidden(newResponses, field)) return;

      const { title } = configProps;
      if (newResponses[fieldId]?.value === title) return;

      didResponseChange = true;
      newResponses[fieldId] = { value: title };
    });
  });

  return { responses: newResponses, didResponseChange };
};

export const clearConditionalResponses = ({
  responses,
  sections,
}) => {
  const newResponses = { ...responses };

  const fieldsToDelete = [];
  let didResponseChange = false;

  sections.forEach(({
    fields = [],
    settings: {
      hasConditionalRendering: sectionHasConditionalRendering,
      conditionalRenderingFormula: sectionConditionalRenderingFormula,
    } = {},
  }) => {
    const isSectionConditionallyHidden = (
      sectionHasConditionalRendering
        && FormHelpers.isConditionalRenderingFormulaComplete(sectionConditionalRenderingFormula)
        && !FormHelpers.executeConditionalRenderingFormula({
          formula: sectionConditionalRenderingFormula,
          responses,
        })
    );

    fields.forEach(({ id: fieldId, configProps }) => {
      const { hasConditionalRendering, conditionalRenderingFormula } = configProps;

      if (
        isSectionConditionallyHidden
        || (
          hasConditionalRendering
            && FormHelpers.isConditionalRenderingFormulaComplete(conditionalRenderingFormula)
            && !FormHelpers.executeConditionalRenderingFormula({
              formula: conditionalRenderingFormula,
              responses,
            })
        )
      ) {
        fieldsToDelete.push(fieldId);
        if (!isNullOrUndefined(responses[fieldId])) didResponseChange = true;
      }
    });
  });

  if (fieldsToDelete.length > 0) {
    fieldsToDelete.forEach((fieldId) => {
      delete newResponses[fieldId];
    });
  }

  return {
    responses: newResponses,
    hiddenFields: fieldsToDelete,
    didResponseChange,
  };
};

export const applyConditionalResponseCleanup = ({
  responses,
  sections,
  sectionPermissionMap,
}) => {
  const cleared = clearConditionalResponses({ responses, sections });
  const synced = syncHiddenSectionStaticTextResponses({
    responses: cleared.responses,
    sections,
    sectionPermissionMap,
  });

  return {
    responses: synced.responses,
    hiddenFields: cleared.hiddenFields,
    didResponseChange: cleared.didResponseChange || synced.didResponseChange,
  };
};

export const getProjectPropStrings = ({
  projects = [],
  projectIdMap = {},
} = {}) => {
  const names = [];
  const numbers = [];
  projects.forEach((projectId) => {
    const {
      [projectId]: {
        name,
        number,
      } = {},
    } = projectIdMap;
    if (name) names.push(name);
    if (number) numbers.push(number);
  });
  return {
    projectNames: names.join(', '),
    projectNumbers: numbers.join(', '),
  };
};

export const renderProjectColumn = ({
  projects = [],
  projectIdMap = {},
  settings = {},
  field,
}) => {
  const projectLabels = projects.map((project) => {
    const {
      [project]: {
        name,
        number,
      } = {},
    } = projectIdMap;
    if (field === 'name') return name;
    if (field === 'number') return number;
    return formatProjectLabelFromCompanySettings({ name, number, settings });
  });
  const projectString = projectLabels.join(', ');
  return (
    <Popover
      placement="top"
      content={<div style={{ width: 250 }}>{projectString}</div>}
    >
      <div>{projectString}</div>
    </Popover>
  );
};

export const renderPreloadedIntoColumn = ({
  preloadedInto = [],
  onFormClick,
}) => {
  if (!preloadedInto?.length) return null;

  const preloadedIntoButtons = preloadedInto.map(({ formId, templateName, number }) => (
    <Button
      key={formId}
      type="button"
      className="link-button"
      style={{ width: 'auto' }}
      onClick={(e) => {
        e.stopPropagation();
        onFormClick?.({ id: formId });
      }}
    >
      <span className="link-button-text">
        {FormHelpers.formatFormName({ templateName, number })}
      </span>
    </Button>
  ));

  return (
    <Popover
      placement="top"
      content={
        <div style={{ width: 250 }}>{preloadedIntoButtons}</div>
      }
    >
      <div>{preloadedIntoButtons[0]}</div>
    </Popover>
  );
};

export const getFormDisplayFieldColumns = ({
  relevantTemplate,
  fieldMap,
}) => (
  relevantTemplate?.displayFields
    ?.sort((a, b) => a.orderIndex - b.orderIndex)
    ?.map(({ fieldId, columnKey }) => {
      let fieldTitle = fieldMap[fieldId]?.configProps?.title;
      if (columnKey) {
        const columnField = fieldMap[fieldId]
          ?.configProps
          ?.columns
          ?.find(({ key }) => key === columnKey);
        const columnName = columnField?.name;
        fieldTitle = `${fieldTitle} - ${columnName}`;
      }
      const colProps = {
        title: fieldTitle,
        dataIndex: columnKey ? `${fieldId}-${columnKey}` : fieldId,
        key: columnKey ? `${fieldId}-${columnKey}` : fieldId,
        render: (value) => {
          if (value) {
            return value;
          }
          return '-';
        },
      };

      return colProps;
    })
);

export const loadForms = async ({
  projectId,
  formTemplateId,
  formTemplateIds,
  startTime,
  endTime,
  formSubmissionIds,
  filterByTimeTrackingGridDate = false,
}) => {
  try {
    const formTemplates = formTemplateId
      ? [].concat(formTemplateId)
      : formTemplateIds;

    const query = {
      getData: true,
      ...(Array.isArray(formSubmissionIds) && formSubmissionIds.length
        ? { formSubmissionIds }
        : {}),
      ...(projectId ? { projectId } : {}),
      ...(formTemplates?.length ? { formTemplates } : {}),
      ...(startTime ? { submittedStartTime: startTime } : {}),
      ...(endTime ? { submittedEndTime: endTime } : {}),
      ...(filterByTimeTrackingGridDate ? { filterByTimeTrackingGridDate: true } : {}),
    };

    const { data: forms = [] } = await axios.get('/forms', {
      params: query,
      paramsSerializer: qs.stringify,
    });

    return forms;
  } catch (err) {
    Sentry.captureException(err);
    message.error('Forms could not be loaded. Please try again');
    return [];
  }
};
export const getHasColumnPermissions = ({
  columnPermissions = {},
  column,
  userPositionId,
  userId,
}) => {
  const colKeyPermissions = columnPermissions[column.key] || {};
  const permArray = Object.keys(colKeyPermissions);
  if (!permArray.length) return true;
  const permSet = new Set(permArray);
  return permSet.has(userPositionId?.toString()) || permSet.has(userId);
};

export const getTemplateDropdownDataTypes = (template = {}) => {
  if (!template) return [];

  const dataTypes = new Set();
  const {
    formData: {
      sections = [],
    } = {},
  } = template;

  sections?.forEach(({ fields = [] }) => {
    fields.forEach(({ selectedType, configProps: { dataType } = {} }) => {
      if (selectedType === 'dropdown' && dataType) {
        dataTypes.add(dataType);
      }
    });
  });

  return Array.from(dataTypes);
};

const MAX_FILTER_LENGTH = 100;

const setUpFormFilter = ({
  id,
  name,
  set,
  filters,
}) => {
  if (id
    && !set.has(id)
    && set.size < MAX_FILTER_LENGTH
  ) {
    filters.push({ text: name, value: id });
    set.add(id);
  }
};

export const getFormListAndFilters = ({
  forms = [],
  isPayable,
  userMap = {},
  vendorMap = {},
  projectIdMap = {},
  customers = {},
  hideDate,
  shouldApplyRange = false,
  range,
}) => {
  const vFilters = [];
  const vendorFilterSet = new Set();
  const cFilters = [];
  const customerFilterSet = new Set();
  const pFilters = [];
  const projectFilterSet = new Set();
  const uFilters = [];
  const userFilterSet = new Set();
  const tFilters = [];
  const templateFilterSet = new Set();
  const sFilters = [];
  const statusFilterSet = new Set();
  const cardFilterList = [];
  const cardFilterSet = new Set();
  const newForms = forms.map((form) => {
    const {
      templateName,
      status,
      statusId,
      users = [],
      vendors = [],
      userId,
      card,
      projects: formProjects = [],
      customers: formCustomers = [],
    } = form;
    const fullUsers = removeDuplicateArrayValues(users.concat([userId])).filter((u) => !!u);
    const userNameList = new Set();
    fullUsers.forEach((assignedUserId) => {
      const {
        [assignedUserId]: { name: userName } = {},
      } = userMap;
      if (userName) userNameList.add(userName);
      if (userName
        && !userFilterSet.has(assignedUserId)
        && userFilterSet.size < MAX_FILTER_LENGTH) {
        uFilters.push({ text: userName, value: assignedUserId });
        userFilterSet.add(assignedUserId);
      }
    });
    const vendorNameList = new Set();
    if (isPayable) {
      vendors.forEach((vendorId) => {
        const {
          [vendorId]: { name: vendorName } = {},
        } = vendorMap ?? {};
        if (vendorName) vendorNameList.add(vendorName);
        if (vendorName
          && !vendorFilterSet.has(vendorId)
          && vendorFilterSet.size < MAX_FILTER_LENGTH
        ) {
          vFilters.push({ text: vendorName, value: vendorId });
          vendorFilterSet.add(vendorId);
        }
      });
    }

    const { projectNames, projectNumbers } = getProjectPropStrings({
      projects: formProjects,
      projectIdMap,
    });
    formProjects.forEach((projectId) => {
      const {
        [projectId]: {
          name: projectName,
        } = {},
      } = projectIdMap;

      setUpFormFilter({
        id: projectId,
        name: projectName,
        set: projectFilterSet,
        filters: pFilters,
      });
    });

    const customerNames = [];
    formCustomers.forEach((customerId) => {
      const {
        [customerId]: {
          name: customerName,
        } = {},
      } = customers;

      customerNames.push(customerName);
      setUpFormFilter({
        id: customerId,
        name: customerName,
        set: customerFilterSet,
        filters: cFilters,
      });
    });

    setUpFormFilter({
      id: templateName,
      name: templateName,
      set: templateFilterSet,
      filters: tFilters,
    });

    if (status
      && statusId
      && !statusFilterSet.has(statusId)
      && statusFilterSet.size < MAX_FILTER_LENGTH
    ) {
      sFilters.push({ text: toTitleCase(status), value: statusId });
      statusFilterSet.add(statusId);
    }

    setUpFormFilter({
      id: card,
      name: card,
      set: cardFilterSet,
      filters: cardFilterList,
    });

    const preloadedIntoText = form.preloadedInto?.length
      ? form.preloadedInto.map(FormHelpers.formatFormName).join(', ')
      : '';

    return {
      ...form,
      createdAtDate: form.createdAt
        ? DateTime.fromMillis(form.createdAt).toLocaleString(DateTime.DATETIME_MED)
        : null,
      initiatedOnDate: form.initiatedOn
        ? DateTime.fromMillis(form.initiatedOn).toLocaleString(DateTime.DATETIME_MED)
        : null,
      lastUpdatedDate: form.lastUpdated
        ? DateTime.fromMillis(form.lastUpdated).toLocaleString(DateTime.DATETIME_MED)
        : null,
      users: fullUsers,
      customer: customerNames.join(', '),
      projectNames,
      projectNumbers,
      userNameList,
      userNames: Array.from(userNameList).join(', '),
      vendorNameList,
      preloadedIntoText,
    };
  })
    .filter((form) => {
      const applyRange = !hideDate || shouldApplyRange;
      if (!applyRange || !range || range.length < 2) return true;
      const ts = form.lastUpdated ?? form.createdAt;
      return ts >= range[0] && ts <= range[1];
    });
  newForms.sort((a, b) => b.lastUpdated - a.lastUpdated);
  return {
    formList: newForms,
    vendorFilters: vFilters,
    customerFilters: cFilters,
    projectFilters: pFilters,
    userFilters: uFilters,
    templateFilters: tFilters,
    statusFilters: sFilters,
    cardFilters: cardFilterList,
  };
};

export const getBucketTemplateTableColumns = ({
  data: {
    customFields = [],
  } = {},
  additionalBaseOptions = {},
  type,
  attachTypeToKey = false,
} = {}) => {
  const nameKey = attachTypeToKey ? `${type}.name` : 'name';
  const descriptionKey = attachTypeToKey ? `${type}.description` : 'description';
  const statusKey = attachTypeToKey ? `${type}.status` : 'status';
  const baseOptions = {
    [nameKey]: 'Name',
    [descriptionKey]: 'Description',
    [statusKey]: 'Status',
    ...additionalBaseOptions,
  };

  customFields?.fields
    ?.filter((field) => textBasedFieldTypes.has(field.type) && field.type !== 'dropdown' && field.type !== 'dateRange')
    ?.forEach((field) => {
      const key = attachTypeToKey ? `${type}.${field.id}` : field.id;
      baseOptions[key] = field.title;
    });

  return baseOptions;
};

// Field types whose response values can contain per-row projectId fields.
// Only these types need to be scanned to collect project IDs for costcode fetching.
const PROJECT_ID_TABLE_TYPES = new Set(['TimeEntry', 'CostCodes', 'Equipment', 'Shifts', 'Materials']);

/**
 * Scans current form responses and sections to collect all projectIds referenced
 * by field types that embed per-row project data. Used by FormResponder to determine
 * which project costcodes to lazily fetch.
 *
 * @param {object[]} sections - form sections array (each with fields[])
 * @param {object} responses - current responses keyed by fieldId
 * @returns {string[]} deduplicated array of projectIds
 */
export const scanResponseProjectIds = ({ sections = [], responses = {} }) => {
  const projectIdSet = new Set();

  sections.forEach(({ fields = [] }) => {
    fields?.forEach(({
      id: fieldId,
      selectedType,
      type,
      configProps = {},
    }) => {
      const response = responses[fieldId];
      if (!response) return;
      const fieldType = selectedType || type;

      if (fieldType === 'dropdown' && configProps.dataType === 'Projects') {
        (response.values || []).forEach((value) => {
          if (value?.id) projectIdSet.add(value.id);
        });
        return;
      }

      if (fieldType === 'table') {
        const {
          dataType,
          linkProjectCostCodes,
          linkProjectCostCodesDropdown,
        } = configProps;
        if (!PROJECT_ID_TABLE_TYPES.has(dataType)) return;

        // If linking project costcodes to table rows, add the linked project ID to the set
        if (linkProjectCostCodes) {
          const linkedProjectId = getLinkedProjectIdForCostCodes({
            linkProjectCostCodes,
            linkProjectCostCodesDropdown,
            responses,
          });
          if (linkedProjectId) projectIdSet.add(linkedProjectId);
        }

        const { values = [] } = response;
        values.forEach((row) => {
          if (row.projectId) projectIdSet.add(row.projectId);
          // Equipment rows also carry currentProjectId / toProjectId
          if (row.currentProjectId) projectIdSet.add(row.currentProjectId);
          if (row.toProjectId) projectIdSet.add(row.toProjectId);
        });
      } else if (fieldType === 'timeTrackingGrid') {
        // Grid stores project context per column as { project: { id } }
        const { columns = [] } = response;
        columns.forEach((col) => {
          if (col.project?.id) projectIdSet.add(col.project.id);
        });
      }
    });
  });

  return Array.from(projectIdSet);
};

export const parseAssignedForm = ({
  form = {},
  type,
  assignedDrafts = {},
  templates = {},
  projectIdMap = {},
  customerIdMap = {},
}) => {
  const {
    id: formId,
    templateId,
    customers = [],
    projects = [],
  } = form;
  const {
    [templateId]: {
      name: templateName,
    } = {},
  } = templates;

  const fullForm = {
    ...form,
    templateName,
    state: type,
  };

  let relevantProjects = projects;
  let relevantCustomers = customers;

  if (formId in assignedDrafts) {
    fullForm.lastUpdated = assignedDrafts[formId].lastUpdated;
    fullForm.state = 'Draft';
    relevantProjects = assignedDrafts[formId].projects;
    relevantCustomers = assignedDrafts[formId].customers;
  }

  const { projectNames, projectNumbers } = getProjectPropStrings({
    projects: relevantProjects,
    projectIdMap,
  });

  const customerNames = [];
  relevantCustomers.forEach((customerId) => {
    const {
      [customerId]: {
        name: customerName,
      } = {},
    } = customerIdMap;
    customerNames.push(customerName);
  });

  fullForm.projectNames = projectNames;
  fullForm.projectNumbers = projectNumbers;
  fullForm.customer = customerNames.join(', ');
  const { lastUpdatedDate } = parseFormDates(fullForm);
  fullForm.lastUpdatedDate = lastUpdatedDate;
  return fullForm;
};

export const parseDraft = ({
  draft,
  customers = {},
  projectIdMap = {},
  templates = {},
}) => {
  const { templateId, customers: formCustomers = [], projects: formProjects = [] } = draft;
  const {
    [templateId]: template = {},
  } = templates;
  const customerNames = formCustomers.map((id) => customers[id]?.name).filter(Boolean);
  const { projectNames, projectNumbers } = getProjectPropStrings({
    projects: formProjects,
    projectIdMap,
  });
  const data = {
    ...draft,
    templateName: template.name,
    customer: customerNames.join(', '),
    projectNames,
    projectNumbers,
    isDraft: true,
  };
  const { lastUpdatedDate } = parseFormDates(data);
  data.lastUpdatedDate = lastUpdatedDate;
  return data;
};

export const getParsedDrafts = ({
  manualDrafts = {},
  assignedDrafts = {},
  customers = {},
  projectIdMap = {},
  templates = {},
}) => {
  const allDrafts = [];
  const selfDrafts = [];
  const assignmentDrafts = [];
  Object.values(manualDrafts).forEach((draft) => {
    const data = parseDraft({ draft, customers, projectIdMap, templates });
    data.source = DRAFT_SOURCE_SELF;
    allDrafts.push(data);
    selfDrafts.push(data);
  });
  Object.values(assignedDrafts).forEach((draft) => {
    const data = parseDraft({ draft, customers, projectIdMap, templates });
    data.source = DRAFT_SOURCE_ASSIGNMENT;
    allDrafts.push(data);
    assignmentDrafts.push(data);
  });
  return {
    allDrafts: allDrafts.sort((a, b) => b.lastUpdated - a.lastUpdated),
    selfDrafts: selfDrafts.sort((a, b) => b.lastUpdated - a.lastUpdated),
    assignmentDrafts: assignmentDrafts.sort((a, b) => b.lastUpdated - a.lastUpdated),
  };
}

export const MANUAL_FAVORITE_TAB = 'favorite';
export const MANUAL_PROJECT_TAB = 'project';
export const MANUAL_GLOBAL_TAB = 'global';

export const getManualFormsTableData = ({
  assignedFormTemplates = [],
  tab,
  searchStr,
  projectIdMap = {},
}) => {
  const assignedTemplateMap = getIdMap(assignedFormTemplates);
  const rows = (assignedFormTemplates || [])
  .filter(({
    active, notDeleted, isFavorite, projectId,
  }) => {
    if (!active || !notDeleted) return false;
    if (tab === MANUAL_FAVORITE_TAB) return isFavorite;
    if (tab === MANUAL_PROJECT_TAB) return projectId;
    return !projectId;
  })
  .map((template) => {
    const { id: templateId, timestamp, projectId } = template;
    const {
      [templateId]: {
        name: templateName,
      } = {},
    } = assignedTemplateMap;
    const {
      [projectId]: {
        name: projectName,
        number: projectNumber,
      } = {},
    } = projectIdMap;
    const { createdAtDate } = parseFormDates({ createdAt: timestamp });
    return {
      ...template,
      templateName,
      createdAt: timestamp,
      projectNames: projectName,
      projectNumbers: projectNumber,
      createdAtDate,
    };
  })
  .filter(({ name, projectNames }) => (
    !searchStr
    || (name && includesTerm(name, searchStr))
    || (projectNames && includesTerm(projectNames, searchStr))
  ));
  rows.sort(sortByString('name'));
  return rows;
};

export const getAssignedToMyselfForms = ({
  assignedForms = [],
  assignedDrafts = {},
  manualDrafts = {},
  templates = {},
  projectIdMap = {},
  customerIdMap = {},
  includeManualDrafts = true,
}) => {
  const fullData = [];
  if (includeManualDrafts) {
    Object.values(manualDrafts).forEach((draft) => {
      const { templateId } = draft;
      const { [templateId]: template = {} } = templates;
      const parsed = parseDraft({
        draft,
        customers: customerIdMap,
        projectIdMap,
        templates,
      });
      fullData.push({
        ...template,
        ...parsed,
        state: 'Draft',
      });
    });
  }

  assignedForms.forEach((form) => {
    fullData.push(parseAssignedForm({
      form,
      type: form.editable ? 'Editing' : 'Assigned',
      assignedDrafts,
      templates,
      projectIdMap,
      customerIdMap,
    }));
  });

  return fullData.sort((a, b) => (b.lastUpdated ?? 0) - (a.lastUpdated ?? 0));
};
